import { BudgetLineDto } from 'api/budgets/models/BudgetDto';
import { BudgetPDFVisibility } from 'api/elements/Models/ElementDto';
import { Action, ADD_LINE, AddLinePlacement, BudgetData, BudgetState, CALCULATE_ALL_LINES_TOTALS, CALCULATE_BUDGET_TOTALS, CALCULATE_LINE_TOTALS, CLONE_LINE, COPY_LINES, CopyType, CUT_LINES, HYDRATE_CLIPBOARD, MOVE_LINES_DOWN, MOVE_LINES_UP, PASTE_LINES, RECALCULATE_LINE_PARAMETER, REMOVE_LINES, REMOVE_LINE_CHILDREN, SELECT_RANGE_TO, SET_BUDGET, SET_CALCULATING, SET_LINES, SET_OPEN_LINES_INDEXES, SET_SELECTED_LINE_KEYS, TOGGLE_SELECTED_LINE_KEY, UPDATE_ALL_LINES, UPDATE_BUDGET, UPDATE_LINE, UPDATE_LINE_PARAMETER, UPDATE_LINE_QUANTITY, UPDATE_LINES_EXPRESSIONS_VALUES, UPDATE_LINES_NUMBERS } from './type';
import { AtLeast } from 'common/types/Atleast';
import { BudgetLineType } from 'api/budgets/enums/BudgetLineType';
import Utils from 'common/services/Utils';
import MathUtils from 'common/services/MathUtils';
import { BudgetLineParameterDto, BudgetLineParameterElementDto } from 'api/budgets/models/BudgetLineParameterDto';
import { ParameterSectionParameterType, ParameterSectionType } from 'common/components/parametersSections/models';
import ExpressionSystem, { ExpressionParameter } from 'common/components/expressionButton/ExpressionSystem';
import { getCatalogParametersCache } from 'screens/business/budgets/budget/lines/utils';

const initialState: BudgetState = {
    selectedLineKeys: [],
    selectionAnchorKey: null,
    copiedLineKeys: [],
    copyType: null,
    lines: [],
    tempLines: [],
    budget: {
        totalNetExcludingTax: 0,
        totalTax: 0,
        totalIncludingTax: 0,
        totalExcludingTax: 0,
        generalitiesTotalExcludingTax: 0,
        generalitiesTotalTax: 0,
        linesTotalNetExcludingTax: 0,
        linesTotalExcludingTax: 0,
        linesTotalTax: 0,
        linesTotalIncludingTax: 0,
        discount: 0,
        discountTaxId: null,
        decimalPlaces: 0,
        defaultMarginPercentage: undefined,
        defaultCommissionMarginPercentage: undefined,
        decennialInsuranceExpression: undefined,
        commissionExpression: undefined,
        configs: []
    },
    openLinesIndexes: [],
    calculating: false,
};

function rootReducer(state: BudgetState = initialState, action: Action): BudgetState {
    switch (action.type) {
        case SET_SELECTED_LINE_KEYS:
            return {
                ...state,
                selectedLineKeys: action.selectedLineKeys ?? [],
                selectionAnchorKey: action.selectionAnchorKey ?? null,
            };

        case TOGGLE_SELECTED_LINE_KEY: {
            const current = state.selectedLineKeys;
            const next = current.includes(action.lineKey)
                ? current.filter(k => k !== action.lineKey)
                : [...current, action.lineKey];
            return {
                ...state,
                selectedLineKeys: next,
                selectionAnchorKey: action.lineKey,
            };
        }

        case SELECT_RANGE_TO: {
            const anchor = state.selectionAnchorKey;
            if (!anchor) {
                return {
                    ...state,
                    selectedLineKeys: [action.lineKey],
                    selectionAnchorKey: action.lineKey,
                };
            }
            const anchorIndex = state.lines.findIndex(l => l.key === anchor);
            const targetIndex = state.lines.findIndex(l => l.key === action.lineKey);
            if (anchorIndex < 0 || targetIndex < 0) {
                return {
                    ...state,
                    selectedLineKeys: [action.lineKey],
                    selectionAnchorKey: action.lineKey,
                };
            }
            const anchorParent = state.lines[anchorIndex].parentKey ?? null;
            const [from, to] = anchorIndex <= targetIndex
                ? [anchorIndex, targetIndex]
                : [targetIndex, anchorIndex];
            // Only siblings at the anchor's level — descendants of intermediate
            // lines are excluded.
            const rangeKeysVisual = state.lines
                .slice(from, to + 1)
                .filter(l => (l.parentKey ?? null) === anchorParent)
                .map(l => l.key);
            // Place the just-clicked target last so it becomes the primary (menu
            // anchor) regardless of shift-click direction.
            const rangeKeys = rangeKeysVisual.includes(action.lineKey)
                ? [...rangeKeysVisual.filter(k => k !== action.lineKey), action.lineKey]
                : rangeKeysVisual;
            return {
                ...state,
                selectedLineKeys: rangeKeys.length > 0 ? rangeKeys : [anchor],
            };
        }

        case SET_LINES:
            return {
                ...state,
                lines: action.lines || [],
            };

        case SET_BUDGET:
            return {
                ...state,
                budget: action.budget,
            };

        case UPDATE_LINE:
            return {
                ...state,
                lines: updateLine(state.lines, action.line),
            };

        case ADD_LINE: {
            const newLines = addLine(state.lines, action.lineKey, action.placement, action.lineType, action.newLineKey);
            if (action.newLineKey) {
                const idx = newLines.findIndex(l => l.key === action.newLineKey);
                if (idx > -1 && isTopLevelElementLine(newLines[idx], newLines)) {
                    newLines[idx] = {
                        ...newLines[idx],
                        marginPercentage: (state.budget.defaultMarginPercentage ?? 0) + (state.budget.defaultCommissionMarginPercentage ?? 0),
                        marginPercentageExpression: ExpressionSystem.serializeParameterMentionId(
                            ExpressionSystem.formatParameterId(['budget', 'defaultCombinedMarginPercentage'])),
                        discountPercentage: state.budget.defaultDiscountPercentage ?? 0,
                        discountPercentageExpression: ExpressionSystem.serializeParameterMentionId(
                            ExpressionSystem.formatParameterId(['budget', 'defaultDiscountPercentage'])),
                        decennialInsurancePercentage: state.budget.defaultDecennialInsurancePercentage ?? 0,
                        decennialInsurancePercentageExpression: ExpressionSystem.serializeParameterMentionId(
                            ExpressionSystem.formatParameterId(['budget', 'defaultDecennialInsurancePercentage'])),
                    };
                }
            }
            return { ...state, lines: newLines };
        }

        case REMOVE_LINES: {
            let next = state.lines;
            for (const key of action.lineKeys) {
                next = removeLine(next, key);
            }
            const removedSet = new Set(action.lineKeys);
            return {
                ...state,
                lines: next,
                selectedLineKeys: state.selectedLineKeys.filter(k => !removedSet.has(k)),
                selectionAnchorKey: state.selectionAnchorKey && removedSet.has(state.selectionAnchorKey)
                    ? null
                    : state.selectionAnchorKey,
                copiedLineKeys: state.copiedLineKeys.filter(k => !removedSet.has(k)),
            };
        }

        case REMOVE_LINE_CHILDREN:
            return {
                ...state,
                lines: removeLineChildren(state.lines, action.parentLineKey),
            };

        case CLONE_LINE:
            return {
                ...state,
                lines: cloneLine(state.lines, action.lineKey, action.newLineKey),
            };

        case COPY_LINES:
            return {
                ...state,
                copiedLineKeys: action.lineKeys,
                copyType: action.lineKeys.length ? 'copy' : null,
                tempLines: action.lineKeys.length ? [...state.lines] : [],
            };

        case CUT_LINES:
            return {
                ...state,
                copiedLineKeys: action.lineKeys,
                copyType: action.lineKeys.length ? 'cut' : null,
                tempLines: action.lineKeys.length ? [...state.lines] : [],
            };

        case PASTE_LINES:
            return {
                ...state,
                lines: pasteLines(state.lines, state.tempLines, action.targetLineKey, state.copiedLineKeys, action.newKeysByOldKey, state.copyType),
                copiedLineKeys: state.copyType === 'cut' ? [] : state.copiedLineKeys,
                copyType: state.copyType === 'cut' ? null : state.copyType,
            };

        case HYDRATE_CLIPBOARD:
            return {
                ...state,
                copiedLineKeys: action.copiedLineKeys,
                copyType: action.copyType,
                tempLines: action.tempLines,
            };

        case CALCULATE_LINE_TOTALS:
            return {
                ...state,
                lines: calculateLineTotals(state.lines, action.lineKey, state.budget.decimalPlaces),
            };
        case CALCULATE_ALL_LINES_TOTALS: {
            // Pass 1: totals (so computed fields have values)
            const totalsPass1 = calculateAllLinesTotals(state.lines, state.budget.decimalPlaces);
            // Pass 2: expressions (see computed fields from pass 1)
            const expressionsPass1 = updateLinesExpressionValues(totalsPass1, state.budget);
            // Pass 3: totals (reflect expression-driven changes)
            const totalsPass2 = calculateAllLinesTotals(expressionsPass1, state.budget.decimalPlaces);

            // Only run passes 4+5 when some expression references computed total fields
            // (e.g. M_<line>__totalExcludingTax_E). Otherwise passes 4+5 are no-ops.
            let finalLines: BudgetLineDto[];
            if (totalsPass2.some(expressionReferencesComputedFields)) {
                const expressionsPass2 = updateLinesExpressionValues(totalsPass2, state.budget);
                finalLines = calculateAllLinesTotals(expressionsPass2, state.budget.decimalPlaces);
            } else {
                finalLines = totalsPass2;
            }

            return {
                ...state,
                lines: finalLines,
            };
        }
        case CALCULATE_BUDGET_TOTALS:
            return {
                ...state,
                budget: calculateBudgetTotals(state, state.budget.decimalPlaces),
            };
        case UPDATE_BUDGET:
            return {
                ...state,
                budget: {
                    ...state.budget,
                    ...action.budget,
                }
            };
        case UPDATE_LINE_QUANTITY:
            return {
                ...state,
                lines: updateLineQuantity(state.lines, action.lineKey),
            };
        case UPDATE_ALL_LINES:
            return {
                ...state,
                lines: updateAllLines(state.lines, action.line),
            };
        case UPDATE_LINE_PARAMETER: {
            // Light update: just set the parameter value (no recalculation)
            const updatedParamLines = updateLineParameterOnly(state.lines, action.lineKey, action.parameter);
            return {
                ...state,
                lines: updatedParamLines,
            };
        }
        case RECALCULATE_LINE_PARAMETER: {
            const recalcLines = recalculateLineAfterParameterChange(state.lines, action.lineKey);
            const recalcWithDescendants = recalculateDescendantExpressions(recalcLines, action.lineKey);
            const recalcAllExpressions = updateLinesExpressionValues(recalcWithDescendants, state.budget);
            const recalcTotalLines = calculateAllLinesTotals(recalcAllExpressions, state.budget.decimalPlaces);

            // Only run 2nd pass if any expression references computed total fields
            let recalcFinalTotals: BudgetLineDto[];
            if (recalcTotalLines.some(l => expressionReferencesComputedFields(l))) {
                const recalcSecondPass = updateLinesExpressionValues(recalcTotalLines, state.budget);
                recalcFinalTotals = calculateAllLinesTotals(recalcSecondPass, state.budget.decimalPlaces);
            } else {
                recalcFinalTotals = recalcTotalLines;
            }

            const recalcState = { ...state, lines: recalcFinalTotals, calculating: false };
            return {
                ...recalcState,
                budget: calculateBudgetTotals(recalcState, state.budget.decimalPlaces),
            };
        }
        case SET_CALCULATING:
            return {
                ...state,
                calculating: action.calculating,
            };
        case UPDATE_LINES_NUMBERS:
            return {
                ...state,
                lines: updateLinesNumbers(state.lines),
            };
        case UPDATE_LINES_EXPRESSIONS_VALUES: {
            const newLines = updateLinesExpressionValues(state.lines, state.budget);
            return {
                ...state,
                lines: newLines,
            };
        }
        case MOVE_LINES_UP:
            return {
                ...state,
                lines: moveLinesGroup(state.lines, action.lineKeys, 'up'),
            };
        case MOVE_LINES_DOWN:
            return {
                ...state,
                lines: moveLinesGroup(state.lines, action.lineKeys, 'down'),
            };
        case SET_OPEN_LINES_INDEXES:
            return {
                ...state,
                openLinesIndexes: action.openLinesIndexes,
            };
        default:
            return state;
    }
}

function updateLine(lines: BudgetLineDto[], updatedLine: AtLeast<BudgetLineDto, 'key'>): BudgetLineDto[] {
    return lines.map(line => {
        if (line.key === updatedLine.key) {
            return {
                ...line,
                ...updatedLine,
            };
        }
        return line;
    });
}

export function createLine(lineType: BudgetLineType, parentKey: string | null, key: string | null): BudgetLineDto {
    return {
        id: null,
        designation: '',
        number: '',
        quantity: null,
        quantityExpression: '',
        grossUnitPrice: 0,
        grossUnitPriceExpression: null,
        discountPercentage: 0,
        discountPercentageExpression: null,
        unitPrice: 0,
        unitPriceExpression: null,
        unitPriceWithChildren: 0,
        totalExcludingTax: 0,
        totalExcludingTaxExpression: null,
        totalUnitExcludingTax: 0,
        totalUnitExcludingTaxExpression: null,
        totalIncludingTax: 0,
        totalTax: 0,
        marginPercentage: 0,
        marginPercentageExpression: null,
        decennialInsurancePercentage: 0,
        decennialInsurancePercentageExpression: null,
        taxValue: 0,
        lineType,
        key: key ?? Utils.newGuid(),
        parentKey,
        isOpen: true,
        parameters: [],
        level: 0,
        changedParameters: false,
        checkedForParameters: false,
        pdfVisibility: lineType === BudgetLineType.ELEMENT
            ? BudgetPDFVisibility.PRICE_INVISIBLE
            : lineType === BudgetLineType.COMPONENT_ELEMENT
                ? BudgetPDFVisibility.ELEMENT_AND_PRICE_VISIBLE
                : null,
        numberOverride: null,
        isCustomElement: false,
        sourceFamilyParameterId: null,
        sourceRequiresTreatment: false,
        absoluteChildrenSum: 0,
    };
}

function addLine(
    lines: BudgetLineDto[],
    lineKey: string | null,
    placement: AddLinePlacement,
    lineType: BudgetLineType,
    newLineKey: string | null
): BudgetLineDto[] {
    let newLines = [...lines];

    // Add to the same level
    if (placement === AddLinePlacement.SAME) {
        const index = lines.findIndex(l => l.key === lineKey);
        if (index > -1) {
            const totalChildren = getTotalLinesInside(lines, lineKey);
            const newLine: BudgetLineDto = createLine(lineType, lines[index].parentKey, newLineKey);
            newLines = [
                ...newLines.slice(0, index + 1 + totalChildren),
                newLine,
                ...newLines.slice(index + 1 + totalChildren),
            ];
        }
    } else if (placement === AddLinePlacement.ROOT) { // Add to root
        const newLine: BudgetLineDto = createLine(lineType, null, newLineKey);
        newLines = [
            ...newLines,
            newLine,
        ];
    } else if (placement === AddLinePlacement.INSIDE) { // Add inside the line
        const index = lines.findIndex(l => l.key === lineKey);
        if (index > -1) {
            const totalChildren = getTotalLinesInside(lines, lineKey);
            const newLine: BudgetLineDto = createLine(lineType, lineKey, newLineKey);
            newLines = [
                ...newLines.slice(0, index + 1 + totalChildren),
                newLine,
                ...newLines.slice(index + 1 + totalChildren),
            ];
        }
    }

    return newLines;
}

// Extract the copied lines as a self-contained forest: each copied root (a copied
// line whose ancestors are not also copied) plus all its descendants, in order.
// Stored as the clipboard `tempLines` so paste works even from a different budget.
// parentKey links inside the forest stay intact; pasteLines identifies the roots via
// `copiedLineKeys` (their parents are absent from the forest).
export function collectCopiedForest(lines: BudgetLineDto[], copiedLineKeys: string[]): BudgetLineDto[] {
    if (!copiedLineKeys.length) {
        return [];
    }
    const childrenByParent = new Map<string, BudgetLineDto[]>();
    const lineByKey = new Map<string, BudgetLineDto>();
    for (const l of lines) {
        lineByKey.set(l.key, l);
        if (l.parentKey == null) continue;
        const arr = childrenByParent.get(l.parentKey);
        if (arr) arr.push(l);
        else childrenByParent.set(l.parentKey, [l]);
    }

    const copiedSet = new Set(copiedLineKeys);
    const hasCopiedAncestor = (lineKey: string): boolean => {
        let parentKey = lineByKey.get(lineKey)?.parentKey;
        while (parentKey) {
            if (copiedSet.has(parentKey)) return true;
            parentKey = lineByKey.get(parentKey)?.parentKey;
        }
        return false;
    };

    const forest: BudgetLineDto[] = [];
    const collect = (line: BudgetLineDto) => {
        forest.push(line);
        for (const child of childrenByParent.get(line.key) ?? []) {
            collect(child);
        }
    };
    for (const root of lines.filter(l => copiedSet.has(l.key) && !hasCopiedAncestor(l.key))) {
        collect(root);
    }
    return forest;
}

function pasteLines(
    lines: BudgetLineDto[],
    tempLines: BudgetLineDto[],
    targetLineKey: string | null,
    copiedLineKeys: string[],
    newKeysByOldKey: Record<string, string>,
    copyType: CopyType
): BudgetLineDto[] {
    if (!copiedLineKeys.length || !tempLines.length || !targetLineKey) {
        return lines;
    }
    const parentLineIndex = lines.findIndex(l => l.key === targetLineKey);
    if (parentLineIndex < 0) {
        return lines;
    }

    // Pre-build parentKey → children index from the snapshot (used for traversal)
    const childrenByParent = new Map<string, BudgetLineDto[]>();
    const lineByKey = new Map<string, BudgetLineDto>();
    for (const l of tempLines) {
        lineByKey.set(l.key, l);
        if (l.parentKey == null) continue;
        const arr = childrenByParent.get(l.parentKey);
        if (arr) arr.push(l);
        else childrenByParent.set(l.parentKey, [l]);
    }

    // Filter copied roots: skip keys whose ancestor is also copied — those will
    // be cloned as part of the ancestor's subtree, not as a separate root.
    const copiedSet = new Set(copiedLineKeys);
    const hasCopiedAncestor = (lineKey: string): boolean => {
        let parentKey = lineByKey.get(lineKey)?.parentKey;
        while (parentKey) {
            if (copiedSet.has(parentKey)) return true;
            parentKey = lineByKey.get(parentKey)?.parentKey;
        }
        return false;
    };
    const orderedRoots = tempLines
        .filter(l => copiedSet.has(l.key) && !hasCopiedAncestor(l.key))
        .map(l => l.key);

    // Build a global keyMapping (oldKey → newKey) covering all roots + descendants
    // BEFORE creating clones, so cross-root expression refs can be rewritten in one pass.
    const keyMapping = new Map<string, string>();
    const assignKeys = (oldKey: string) => {
        const provided = newKeysByOldKey[oldKey];
        keyMapping.set(oldKey, provided ?? Utils.newGuid());
        for (const child of childrenByParent.get(oldKey) ?? []) {
            assignKeys(child.key);
        }
    };
    for (const rootKey of orderedRoots) {
        assignKeys(rootKey);
    }

    // Clone each subtree, placing the root under the target line.
    const cloned: BudgetLineDto[] = [];
    const cloneSubtree = (oldKey: string, newParentKey: string | null) => {
        const old = lineByKey.get(oldKey);
        if (!old) return;
        const newKey = keyMapping.get(oldKey)!;
        cloned.push({
            ...cleanClonedLine(old),
            key: newKey,
            parentKey: newParentKey,
            parentId: undefined,
        });
        for (const child of childrenByParent.get(oldKey) ?? []) {
            cloneSubtree(child.key, newKey);
        }
    };
    for (const rootKey of orderedRoots) {
        cloneSubtree(rootKey, targetLineKey);
    }

    // Rewrite expressions in pasted lines so refs point to new keys
    const rewritten = cloned.map(line => rewriteLineKeyExpressions(line, keyMapping));

    const totalChildren = getTotalLinesInside(lines, targetLineKey);
    let linesResult: BudgetLineDto[] = [
        ...lines.slice(0, parentLineIndex + 1 + totalChildren),
        ...rewritten,
        ...lines.slice(parentLineIndex + 1 + totalChildren),
    ];

    if (copyType === 'cut') {
        for (const oldKey of copiedLineKeys) {
            linesResult = removeLine(linesResult, oldKey);
        }
    }

    return linesResult;
}

function removeLine(stateLines: BudgetLineDto[], lineKey: string): BudgetLineDto[] {
    const removeDescendants = (lines: BudgetLineDto[], parentKey: string): BudgetLineDto[] => {
        const directChildren = lines.filter(line => line.parentKey === parentKey);

        directChildren.forEach(child => {
            lines = removeDescendants(lines, child.key);
        });

        return lines.filter(line => line.parentKey !== parentKey);
    };

    let updatedLines = stateLines.filter(line => line.key !== lineKey);
    updatedLines = removeDescendants(updatedLines, lineKey);

    return updatedLines;
}

function removeLineChildren(stateLines: BudgetLineDto[], parentLineKey: string): BudgetLineDto[] {
    const removeDescendants = (lines: BudgetLineDto[], parentKey: string): BudgetLineDto[] => {
        const directChildren = lines.filter(line => line.parentKey === parentKey);

        directChildren.forEach(child => {
            lines = removeDescendants(lines, child.key);
        });

        return lines.filter(line => line.parentKey !== parentKey);
    };

    let updatedLines = stateLines.filter(line => line.parentKey !== parentLineKey);
    const deletedLines = stateLines.filter(line => line.parentKey === parentLineKey);

    deletedLines.forEach(line => {
        updatedLines = removeDescendants(updatedLines, line.key);
    });

    return updatedLines;
}

export function getTotalLinesInside(lines: BudgetLineDto[], lineKey: string | null) {
    const children = lines.filter(x => x.parentKey === lineKey);

    let totalCount = children.length;

    for (const child of children) {
        totalCount += getTotalLinesInside(lines, child.key);
    }

    return totalCount;
}

function cloneLine(lines: BudgetLineDto[], lineKey: string, newLineKey: string): BudgetLineDto[] {
    let linesResult = [...lines];

    const index = lines.findIndex(l => l.key === lineKey);
    if (index > -1) {
        const totalChildren = getTotalLinesInside(lines, lineKey)

        // Build key mapping: oldKey → newKey (used to rewrite expressions)
        const keyMapping = new Map<string, string>();

        const clonedLine = { ...cleanClonedLine(linesResult[index]), key: newLineKey };
        keyMapping.set(lineKey, newLineKey);

        const remainingLines = linesResult.slice(index + 1 + totalChildren);

        linesResult = [
            ...linesResult.slice(0, index + 1 + totalChildren),
            clonedLine,
        ];

        // Pre-build parentKey → children index (O(n) once vs O(n²) filter per node)
        const childrenByParent = new Map<string, BudgetLineDto[]>();
        for (const l of lines) {
            if (l.parentKey == null) continue;
            const arr = childrenByParent.get(l.parentKey);
            if (arr) arr.push(l);
            else childrenByParent.set(l.parentKey, [l]);
        }

        const cloneDescendants = (parentKey: string, newParentKey: string) => {
            const descendants = childrenByParent.get(parentKey) ?? [];
            descendants.forEach(descendant => {
                const newDescendant = {
                    ...cleanClonedLine(descendant),
                    parentKey: newParentKey,
                };
                keyMapping.set(descendant.key, newDescendant.key);
                linesResult.push(newDescendant);
                cloneDescendants(descendant.key, newDescendant.key);
            });
        };

        cloneDescendants(lineKey, clonedLine.key);

        // Rewrite expressions in cloned lines to reference new keys instead of old ones
        const clonedKeys = new Set(keyMapping.values());
        linesResult = linesResult.map(line => {
            if (!clonedKeys.has(line.key)) return line;
            return rewriteLineKeyExpressions(line, keyMapping);
        });

        linesResult = [
            ...linesResult,
            ...remainingLines,
        ];
    }

    return linesResult;
}

/**
 * Rewrites all expressions in a line, replacing old line keys with new ones.
 * Used after cloning/pasting to ensure expressions reference the cloned lines instead of originals.
 */
export function rewriteLineKeyExpressions(line: BudgetLineDto, keyMapping: Map<string, string>): BudgetLineDto {
    const rewrite = (expr: string | null | undefined): string | null | undefined => {
        if (!expr) return expr;
        let result: string = expr;
        keyMapping.forEach((newKey, oldKey) => {
            const oldPrefix = `M_${oldKey.replace(/[^0-9A-Za-z_]/g, '')}__`;
            const newPrefix = `M_${newKey.replace(/[^0-9A-Za-z_]/g, '')}__`;
            result = result.split(oldPrefix).join(newPrefix);
        });
        return result;
    };

    const rewriteParams = (params: BudgetLineParameterDto[]): BudgetLineParameterDto[] => {
        return params.map(p => ({
            ...p,
            number: p.number?.numberExpression ? { ...p.number, numberExpression: rewrite(p.number.numberExpression) as string | null } : p.number,
            quantity: p.quantity?.quantityExpression ? { ...p.quantity, quantityExpression: rewrite(p.quantity.quantityExpression) as string | null } : p.quantity,
            price: p.price?.priceExpression ? { ...p.price, priceExpression: rewrite(p.price.priceExpression) as string | null } : p.price,
            minMax: p.minMax?.valueExpression ? { ...p.minMax, valueExpression: rewrite(p.minMax.valueExpression) as string | null } : p.minMax,
            children: p.children ? rewriteParams(p.children) : p.children,
        }));
    };

    return {
        ...line,
        quantityExpression: rewrite(line.quantityExpression) as string | null,
        grossUnitPriceExpression: rewrite(line.grossUnitPriceExpression) as string | null,
        marginPercentageExpression: rewrite(line.marginPercentageExpression) as string | null,
        discountPercentageExpression: rewrite(line.discountPercentageExpression) as string | null,
        decennialInsurancePercentageExpression: rewrite(line.decennialInsurancePercentageExpression) as string | null,
        totalUnitExcludingTaxExpression: rewrite(line.totalUnitExcludingTaxExpression) as string | null,
        totalExcludingTaxExpression: rewrite(line.totalExcludingTaxExpression) as string | null,
        parameters: rewriteParams(line.parameters),
    };
}

function cleanClonedLine(line: BudgetLineDto): BudgetLineDto {
    function cleanParameters(parameters: BudgetLineParameterDto[]): BudgetLineParameterDto[] {
        return parameters.map(parameter => {
            return {
                ...parameter,
                id: undefined,
                key: Utils.newGuid(),
                familyId: undefined,
                elementId: undefined,
                elementFamilyId: undefined,
                elementParameterId: undefined,
                budgetLineParameterElementId: null,
                elements: parameter.elements?.map(product => ({
                    ...product,
                    id: null as any,
                    key: Utils.newGuid(),
                    parameters: cleanParameters(product.parameters ?? []),
                })) ?? [],
                minMax: parameter.minMax
                    ? {
                        ...parameter.minMax,
                        id: null as any,
                        elements: parameter.minMax.elements?.map(product => ({
                            ...product,
                            id: null as any,
                            key: Utils.newGuid(),
                            parameters: cleanParameters(product.parameters ?? []),
                        })) ?? [],
                    }
                    : parameter.minMax,
                children: cleanParameters(parameter.children),
            };
        });
    }

    return {
        ...line,
        id: null,
        key: Utils.newGuid(),
        parameters: cleanParameters(line.parameters),
        numberOverride: null,
    }
}

/**
 * Checks if an expression references CURRENT_PARENT_LINE parameters that don't exist
 * in the new parent. Returns null if invalid, the expression otherwise.
 */
function validateParentExpression(expression: string | null | undefined, parentParameterIds: Set<string>): string | null {
    if (!expression) return expression as string | null;

    // Find all CURRENT_PARENT_LINE mentions: M_CURRENT_PARENT_LINE__<id>__parameter_E
    const regex = /M_CURRENT_PARENT_LINE__([0-9A-Za-z_]+)__parameter_E/g;
    let match;
    while ((match = regex.exec(expression)) !== null) {
        const referencedId = match[1];
        if (!parentParameterIds.has(referencedId)) {
            return null; // Invalid reference - clear the expression
        }
    }
    return expression;
}

/**
 * Collects all stripped familyParameterIds from a line's parameters.
 */
function collectStrippedParameterIds(parameters: BudgetLineParameterDto[]): Set<string> {
    const result = new Set<string>();

    function traverse(params: BudgetLineParameterDto[]) {
        for (const param of params) {
            if (param.type === ParameterSectionType.PARAMETER && param.familyParameterId) {
                result.add(param.familyParameterId.replace(/[^0-9A-Za-z_]/g, ''));
            }
            if (param.children) {
                traverse(param.children);
            }
        }
    }

    traverse(parameters);
    return result;
}

/**
 * Clears invalid CURRENT_PARENT_LINE expressions on a pasted line and its parameters.
 * When an expression references parent parameters that don't exist, the expression is cleared
 * and the value is reset to 0.
 */
function clearInvalidParentExpressions(line: BudgetLineDto, parentParameterIds: Set<string>): BudgetLineDto {
    const updatedLine = { ...line };

    // Validate line-level expressions
    if (updatedLine.quantityExpression && !validateParentExpression(updatedLine.quantityExpression, parentParameterIds)) {
        updatedLine.quantityExpression = null;
        updatedLine.quantity = 0;
    }
    if (updatedLine.grossUnitPriceExpression && !validateParentExpression(updatedLine.grossUnitPriceExpression, parentParameterIds)) {
        updatedLine.grossUnitPriceExpression = null;
        updatedLine.grossUnitPrice = 0;
    }
    if (updatedLine.marginPercentageExpression && !validateParentExpression(updatedLine.marginPercentageExpression, parentParameterIds)) {
        updatedLine.marginPercentageExpression = null;
        updatedLine.marginPercentage = 0;
    }
    if (updatedLine.discountPercentageExpression && !validateParentExpression(updatedLine.discountPercentageExpression, parentParameterIds)) {
        updatedLine.discountPercentageExpression = null;
        updatedLine.discountPercentage = 0;
    }
    if (updatedLine.decennialInsurancePercentageExpression && !validateParentExpression(updatedLine.decennialInsurancePercentageExpression, parentParameterIds)) {
        updatedLine.decennialInsurancePercentageExpression = null;
        updatedLine.decennialInsurancePercentage = 0;
    }
    if (updatedLine.totalUnitExcludingTaxExpression && !validateParentExpression(updatedLine.totalUnitExcludingTaxExpression, parentParameterIds)) {
        updatedLine.totalUnitExcludingTaxExpression = null;
        updatedLine.totalUnitExcludingTax = 0;
    }
    if (updatedLine.totalExcludingTaxExpression && !validateParentExpression(updatedLine.totalExcludingTaxExpression, parentParameterIds)) {
        updatedLine.totalExcludingTaxExpression = null;
        updatedLine.totalExcludingTax = 0;
    }

    // Validate parameter expressions
    updatedLine.parameters = clearInvalidParentParameterExpressions(updatedLine.parameters, parentParameterIds);

    return updatedLine;
}

function clearInvalidParentParameterExpressions(parameters: BudgetLineParameterDto[], parentParameterIds: Set<string>): BudgetLineParameterDto[] {
    return parameters.map(param => {
        const updated = { ...param };

        if (updated.number?.numberExpression && !validateParentExpression(updated.number.numberExpression, parentParameterIds)) {
            updated.number = { ...updated.number, numberExpression: null, number: 0 };
        }
        if (updated.quantity?.quantityExpression && !validateParentExpression(updated.quantity.quantityExpression, parentParameterIds)) {
            updated.quantity = { ...updated.quantity, quantityExpression: null, quantity: 0 };
        }
        if (updated.price?.priceExpression && !validateParentExpression(updated.price.priceExpression, parentParameterIds)) {
            updated.price = { ...updated.price, priceExpression: null, price: 0 };
        }
        if (updated.minMax?.valueExpression && !validateParentExpression(updated.minMax.valueExpression, parentParameterIds)) {
            updated.minMax = { ...updated.minMax, valueExpression: null, value: 0 };
        }

        if (updated.children) {
            updated.children = clearInvalidParentParameterExpressions(updated.children, parentParameterIds);
        }

        return updated;
    });
}

export function createLineNumber(levelName: string, i: number | string) {
    return levelName ? `${levelName}.${i.toString()}` : i.toString();
}

function replaceLinesByKey(items: BudgetLineDto[], newItems: BudgetLineDto[]): BudgetLineDto[] {
    const newItemMap = new Map<string, BudgetLineDto>(newItems.map(item => [item.key, item]));

    return items.map(item =>
        newItemMap.has(item.key) ? newItemMap.get(item.key)! : item
    );
}

function calculateAllLinesTotals(lines: BudgetLineDto[], decimalPlaces: number): BudgetLineDto[] {
    let resultLines = [...lines];
    // Root = parentKey is null OR points to a key that doesn't exist in the lines array
    // (orphan subtrees that would otherwise never get their totals recomputed).
    const lineKeys = new Set(resultLines.map(l => l.key));
    const parents = resultLines.filter(x => x.parentKey === null || !lineKeys.has(x.parentKey));

    for (const line of parents) {
        const updatedLines = calculateLineTotalsValues(line, resultLines, decimalPlaces);
        resultLines = replaceLinesByKey(resultLines, updatedLines);
    }

    return resultLines;
}

function calculateLineTotals(lines: BudgetLineDto[], lineKey: string, decimalPlaces: number): BudgetLineDto[] {
    const line = findLineByKey(lineKey, lines);
    if (!line) {
        return lines;
    }

    const rootParent = findRootParent(line, lines);
    if (!rootParent) {
        return lines;
    }

    const linesToUpdate = calculateLineTotalsValues(rootParent, lines, decimalPlaces);

    return replaceLinesByKey(lines, linesToUpdate);
}

function calculateConfigurationProduct(p: BudgetLineParameterElementDto) {
    const productChildren = (p.parameters ?? []).map(calculateConfigurationParameters);

    const childrenTotal = productChildren.reduce((sum, child) => MathUtils.plus(sum, child), 0)
    return MathUtils.plus(MathUtils.multiply(childrenTotal ?? 0, p.quantity ?? 0), MathUtils.multiply(p.price ?? 0, p.quantity ?? 0));
}

function calculateConfigurationParameters(parameter: BudgetLineParameterDto): number {
    const children = (parameter.children ?? []).map(calculateConfigurationParameters);

    let values: number[] = [];

    switch (parameter.parameterType) {
        case ParameterSectionParameterType.PRODUCTS:
            values = (parameter.elements ?? []).filter(p => p.checked).map(calculateConfigurationProduct);
            break;
        case ParameterSectionParameterType.NUMBER_MIN_MAX:
            values = (parameter.minMax?.elements ?? []).filter(p => p.checked).map(calculateConfigurationProduct);
            break;

        default:
            break;
    }

    return MathUtils.plus(...children, ...values);
}

function calculateLineTotalsValues(lineToCalculate: BudgetLineDto, allLines: BudgetLineDto[], decimalPlaces: number): BudgetLineDto[] {
    const updatedLinesMap = new Map<string, BudgetLineDto>();
    const rawTotalsMap = new Map<string, number>();

    // Pre-build parentKey → children index once (O(n)) instead of filtering allLines for every node (O(n²)).
    const childrenByParent = new Map<string, BudgetLineDto[]>();
    for (const l of allLines) {
        if (l.parentKey == null) continue;
        const arr = childrenByParent.get(l.parentKey);
        if (arr) arr.push(l);
        else childrenByParent.set(l.parentKey, [l]);
    }

    const updateLineAndChildren = (currentLine: BudgetLineDto): BudgetLineDto => {
        // Update children (lookup via index instead of filter)
        const children = (childrenByParent.get(currentLine.key) ?? []).map(updateLineAndChildren);

        // Filter children to include only ELEMENT,OUVRAGE and SECTION types
        const childrenTotals = children.filter(l => l.lineType === BudgetLineType.ELEMENT || l.lineType === BudgetLineType.COMPONENT_ELEMENT || l.lineType === BudgetLineType.SECTION || l.lineType === BudgetLineType.COMPONENT);

        // Children with a custom total text are excluded from rollups (they contribute 0
        // to the parent section's total and aggregates), but their real totalExcludingTax
        // is preserved on the line itself so display fields (T.V.U., quantity × price) stay correct.
        const childrenTotalsForRollup = childrenTotals.filter(l => !(typeof l.customTotalText === 'string' && l.customTotalText.length > 0));

        const isSectionLike = currentLine.lineType === BudgetLineType.SECTION;

        // Calculate children sum first (needed for totalCostUnitPrice)
        const childrenAbsoluteSum = childrenTotalsForRollup.reduce(
            (sum, child) => MathUtils.plus(sum, child.totalExcludingTax),
            0
        );
        // Sum of children's non-rounded totals (for "Total Elementos Agregados")
        const childrenAbsoluteSumRaw = childrenTotalsForRollup.reduce(
            (sum, child) => MathUtils.plus(sum, rawTotalsMap.get(child.key) ?? child.totalExcludingTax),
            0
        );
        const unitPriceChildrenSum = (currentLine.quantity ?? 0) > 0
            ? MathUtils.divide(childrenAbsoluteSumRaw, currentLine.quantity ?? 0)
            : 0;

        // 1. Apply discount to gross unit price
        const discountValue = isSectionLike
            ? 0
            : MathUtils.multiply(currentLine.grossUnitPrice, MathUtils.divide(currentLine.discountPercentage, 100)) * -1;

        // Total de Custo Unit. = Preço de Custo Unit. - Desconto + Somatório Elementos Agregados
        const totalCostUnitPrice = MathUtils.plus(currentLine.grossUnitPrice, discountValue, unitPriceChildrenSum);

        // 2. Apply margin to total cost unit price
        const marginValue = isSectionLike
            ? 0
            : MathUtils.multiply(totalCostUnitPrice, MathUtils.divide(currentLine.marginPercentage, 100));

        const priceWithMargin = MathUtils.plus(totalCostUnitPrice, marginValue);
        const decennialInsuranceValue = isSectionLike
            ? 0
            : MathUtils.multiply(priceWithMargin, MathUtils.divide(currentLine.decennialInsurancePercentage, 100));

        const configurationProductsSum = MathUtils.plus(...currentLine.parameters.map(calculateConfigurationParameters));

        const unitPriceRaw = (
            isSectionLike
                ? 0
                : MathUtils.plus(totalCostUnitPrice, marginValue, decennialInsuranceValue, configurationProductsSum)
        );

        // Apply unitPriceExpression (e.g. ROUNDUP) for display value
        const isComponentLine = currentLine.lineType === BudgetLineType.COMPONENT;
        const unitPriceExpression = currentLine.unitPriceExpression
            ?? (isComponentLine ? `ROUNDUP(M_CURRENT_LINE__unitPrice_E; ${decimalPlaces})` : null);
        let unitPrice = unitPriceRaw;
        if (unitPriceExpression) {
            const exprParams: ExpressionParameter[] = [
                { id: ExpressionSystem.formatParameterId(['CURRENT_LINE', 'unitPrice']), name: '', value: unitPriceRaw }
            ];
            const result = ExpressionSystem.evaluate(unitPriceExpression, exprParams);
            if (!result.error) {
                unitPrice = result.value;
            }
        }

        const totalExcludingTax = isSectionLike
            ? childrenTotalsForRollup.reduce((sum, child) => MathUtils.plus(sum, child.totalExcludingTax), 0)
            : MathUtils.multiply(currentLine.quantity ?? 0, unitPrice);

        // Store non-rounded total for parent's raw children sum
        const totalExcludingTaxRaw = isSectionLike
            ? childrenAbsoluteSumRaw
            : MathUtils.multiply(currentLine.quantity ?? 0, unitPriceRaw);
        rawTotalsMap.set(currentLine.key, totalExcludingTaxRaw);

        const totalUnitExcludingTax = isSectionLike
            ? ((currentLine.quantity ?? 0) > 0
                ? MathUtils.divide(totalExcludingTax, currentLine.quantity!)
                : 0)
            : MathUtils.multiply(currentLine.quantity ?? 0, unitPrice);

        const totalTax = isSectionLike
            ? childrenTotalsForRollup.reduce((sum, child) => MathUtils.plus(sum, child.totalTax), 0)
            : childrenTotalsForRollup.reduce((sum, child) => MathUtils.plus(sum, child.totalTax * (child.quantity ?? 0)), MathUtils.multiply(totalUnitExcludingTax, MathUtils.divide(currentLine.taxValue, 100)));

        const lineTotalIncludingTax = MathUtils.multiply(totalExcludingTax, MathUtils.plus(1, MathUtils.divide(currentLine.taxValue, 100)));

        const totalIncludingTax = (
            isSectionLike
                ? childrenTotalsForRollup.reduce((sum, child) => MathUtils.plus(sum, child.totalIncludingTax), 0)
                : (currentLine.quantity ?? 0) > 0
                    // ? MathUtils.plus(
                    //     MathUtils.multiply(
                    //         childrenTotals.reduce((sum, child) => MathUtils.plus(sum, child.totalIncludingTax), 0),
                    //         currentLine.quantity
                    //     ),
                    //     lineTotalIncludingTax
                    // )
                    ? lineTotalIncludingTax
                    : 0
        );

        // Line-level monetary amounts contributed by THIS line alone. Sections contribute nothing
        // themselves; elements contribute their own gross / discount / margin / insurance amounts.
        const qty = currentLine.quantity ?? 0;
        const ownGrossAmount = isSectionLike ? 0 : MathUtils.multiply(currentLine.grossUnitPrice, qty);
        const ownDiscountAmount = isSectionLike ? 0 : MathUtils.multiply(currentLine.grossUnitPrice, MathUtils.divide(currentLine.discountPercentage, 100), qty);
        const ownCostBaseAmount = MathUtils.minus(ownGrossAmount, ownDiscountAmount);
        const ownMarginAmount = isSectionLike ? 0 : MathUtils.multiply(totalCostUnitPrice, MathUtils.divide(currentLine.marginPercentage, 100), qty);
        const ownInsuranceAmount = isSectionLike
            ? 0
            : MathUtils.multiply(
                MathUtils.multiply(totalCostUnitPrice, MathUtils.plus(1, MathUtils.divide(currentLine.marginPercentage, 100))),
                MathUtils.divide(currentLine.decennialInsurancePercentage, 100),
                qty
            );

        // Aggregate = own + sum of direct children's aggregates. Applies to all line types so that
        // element-with-composition correctly propagates composition children's amounts upward.
        // Children with customTotalText are excluded from the rollup (they contribute 0).
        const aggregateGrossAmount = MathUtils.plus(ownGrossAmount, childrenTotalsForRollup.reduce((sum, c) => MathUtils.plus(sum, c.aggregateGrossAmount ?? 0), 0));
        const aggregateDiscountAmount = MathUtils.plus(ownDiscountAmount, childrenTotalsForRollup.reduce((sum, c) => MathUtils.plus(sum, c.aggregateDiscountAmount ?? 0), 0));
        const aggregateCostBaseAmount = MathUtils.plus(ownCostBaseAmount, childrenTotalsForRollup.reduce((sum, c) => MathUtils.plus(sum, c.aggregateCostBaseAmount ?? 0), 0));
        const aggregateMarginAmount = MathUtils.plus(ownMarginAmount, childrenTotalsForRollup.reduce((sum, c) => MathUtils.plus(sum, c.aggregateMarginAmount ?? 0), 0));
        const aggregateInsuranceAmount = MathUtils.plus(ownInsuranceAmount, childrenTotalsForRollup.reduce((sum, c) => MathUtils.plus(sum, c.aggregateInsuranceAmount ?? 0), 0));

        // Lines with customTotalText keep their REAL totals on the line itself (so T.V.U.,
        // quantity × unitPrice and other display fields continue to show meaningful values).
        // They are excluded from the parent rollup above via childrenTotalsForRollup and from
        // the budget-level total in calculateBudgetTotals.

        // Create updated line
        const updatedLine = {
            ...currentLine,
            unitPrice,
            unitPriceExpression,
            unitPriceWithChildren: unitPriceChildrenSum,
            absoluteChildrenSum: childrenAbsoluteSumRaw,
            totalUnitExcludingTax,
            totalExcludingTax,
            totalIncludingTax,
            totalTax,
            aggregateGrossAmount,
            aggregateDiscountAmount,
            aggregateCostBaseAmount,
            aggregateMarginAmount,
            aggregateInsuranceAmount,
        };

        // Store the updated line in the map
        updatedLinesMap.set(updatedLine.key, updatedLine);

        return updatedLine;
    };

    // Start the update from the specific line
    updateLineAndChildren(lineToCalculate);

    // Return updated lines
    const updatedLines = allLines.map(line => {
        return updatedLinesMap.get(line.key) || null;
    }).filter(x => x !== null);

    return updatedLines as BudgetLineDto[];
}

export function findLineByKey(key: string, lines: BudgetLineDto[]): BudgetLineDto | null {
    for (const l of lines) {
        if (l.key === key) {
            return l;
        }
    }
    return null;
}

export function isTopLevelElementLine(line: BudgetLineDto, allLines: BudgetLineDto[]): boolean {
    const elementTypes = [BudgetLineType.ELEMENT, BudgetLineType.COMPONENT_ELEMENT, BudgetLineType.COMPONENT];
    if (!elementTypes.includes(line.lineType)) return false;

    let parentKey = line.parentKey;
    while (parentKey) {
        const parent = findLineByKey(parentKey, allLines);
        if (!parent) break;
        if (elementTypes.includes(parent.lineType)) return false;
        parentKey = parent.parentKey;
    }
    return true;
}

function findRootParent(currentLine: BudgetLineDto, lines: BudgetLineDto[]): BudgetLineDto | null {
    if (!currentLine.parentKey) {
        return currentLine;
    }

    const parentLine = findLineByKey(currentLine.parentKey, lines);
    if (!parentLine) {
        return null;
    }

    return findRootParent(parentLine, lines);
}

function calculateBudgetTotals(state: BudgetState, decimalPlaces: number) {
    let linesNet = 0;
    let linesTax = 0;

    const lineParents = state.lines.filter(x => x.parentKey === null);
    lineParents.forEach(line => {
        // Lines with a user-defined custom total text don't contribute to the budget total.
        if (typeof line.customTotalText === 'string' && line.customTotalText.length > 0) return;
        linesNet = MathUtils.plus(linesNet, line.totalExcludingTax);
        linesTax = MathUtils.plus(linesTax, line.totalTax);
    });

    const discountTaxValue = MathUtils.multiply((state.budget.discount ?? 0), MathUtils.divide(state.budget.discountTaxValue ?? 0, 100));

    // Lines-only totals — referenced by the expression engine (line + generality expressions).
    // Kept separate so generality expressions can reference the budget total without becoming
    // self-referential (which would loop).
    const linesTotalNetExcludingTax = linesNet;
    const linesTotalExcludingTax = MathUtils.minus(linesNet, state.budget.discount ?? 0);
    const linesTotalTax = MathUtils.minus(linesTax, discountTaxValue ?? 0);
    const linesTotalIncludingTax = MathUtils.plus(linesTotalExcludingTax, linesTotalTax);

    // Canonical totals — include the generality subtotals. These feed the footer, the header
    // total and the persisted budget.
    const generalitiesNet = state.budget.generalitiesTotalExcludingTax ?? 0;
    const generalitiesTax = state.budget.generalitiesTotalTax ?? 0;
    const totalNetExcludingTax = MathUtils.plus(linesNet, generalitiesNet);
    const totalExcludingTax = MathUtils.minus(totalNetExcludingTax, state.budget.discount ?? 0);
    const totalTax = MathUtils.minus(MathUtils.plus(linesTax, generalitiesTax), discountTaxValue ?? 0);
    const totalIncludingTax = MathUtils.plus(totalExcludingTax, totalTax);

    return {
        ...state.budget,
        totalNetExcludingTax,
        totalExcludingTax,
        totalIncludingTax,
        totalTax,
        linesTotalNetExcludingTax,
        linesTotalExcludingTax,
        linesTotalTax,
        linesTotalIncludingTax,
    }
}

function getTotalParamQuantity(parameter: BudgetLineParameterDto): number {
    if (parameter.parameterType === ParameterSectionParameterType.QUANTITY) {
        return parameter.quantity?.quantity ?? 0;
    }
    return MathUtils.plus(...(parameter.children ?? []).map(getTotalParamQuantity));
}

function hasTotalParamQuantity(parameters: BudgetLineParameterDto[]): boolean {
    for (const p of parameters) {
        if (p.parameterType === ParameterSectionParameterType.QUANTITY) {
            return true;
        }
        if (p.children && hasTotalParamQuantity(p.children)) {
            return true;
        }
    }
    return false;
}

function updateLineQuantity(lines: BudgetLineDto[], lineKey: string) {
    const line = findLineByKey(lineKey, lines);
    if (!line) {
        return lines;
    }

    const hasQuantityParam = hasTotalParamQuantity(line.parameters ?? []);

    const quantity = hasQuantityParam
        ? MathUtils.plus(...(line.parameters ?? []).map(getTotalParamQuantity))
        : line.quantity;

    const quantityExpression = hasQuantityParam
        ? null
        : line.quantityExpression;

    return updateLine(lines, { key: line.key, quantity, quantityExpression });
}

function updateAllLines(lines: BudgetLineDto[], lineData: Partial<BudgetLineDto>) {
    return lines.map(line => {
        const newLine = {
            ...line,
            ...lineData,
        }
        return newLine;
    });
}

function updateLinesNumbers(stateLines: BudgetLineDto[]): BudgetLineDto[] {
    const lineMap: { [key: string]: BudgetLineDto } = {};

    stateLines.forEach(line => {
        lineMap[line.key] = { ...line, number: '', level: 0 };
    });

    // Pre-build parentKey → children index (O(n) once vs O(n²) filter per node)
    const childrenByParent = new Map<string, BudgetLineDto[]>();
    for (const l of stateLines) {
        if (l.parentKey == null) continue;
        const arr = childrenByParent.get(l.parentKey);
        if (arr) arr.push(l);
        else childrenByParent.set(l.parentKey, [l]);
    }

    function assignLineNumbers(lines: BudgetLineDto[], parentNumber: string, parentLevel: number): void {
        let levelIndex = 0;
        let currentPrefix = parentNumber;
        lines.forEach(line => {
            const isNumberedType = line.lineType === BudgetLineType.SECTION || line.lineType === BudgetLineType.ELEMENT || line.lineType === BudgetLineType.COMPONENT_ELEMENT || line.lineType === BudgetLineType.COMPONENT;
            const override = line.numberOverride;

            if (isNumberedType) {
                if (override === null || override === undefined) {
                    // Auto-generate
                    levelIndex++;
                    lineMap[line.key].number = createLineNumber(currentPrefix, levelIndex);
                } else if (override === '') {
                    // User cleared the number
                    lineMap[line.key].number = '';
                } else {
                    // User set a custom number - use as-is
                    lineMap[line.key].number = override;
                    // Parse last segment for continuation and extract prefix
                    const segments = override.split('.');
                    const lastSegment = segments.pop() ?? '';
                    const parsed = parseInt(lastSegment, 10);
                    if (!isNaN(parsed)) {
                        levelIndex = parsed;
                        currentPrefix = segments.join('.');
                    }
                }
            } else {
                lineMap[line.key].number = createLineNumber(currentPrefix, levelIndex);
            }

            lineMap[line.key].level = parentLevel;

            const children = childrenByParent.get(line.key) ?? [];
            if (children.length > 0) {
                assignLineNumbers(children, lineMap[line.key].number, parentLevel + 1);
            }
        });
    }

    const parents = stateLines.filter(x => x.parentKey === null);
    assignLineNumbers(parents, '', 0);

    return stateLines.map(line => lineMap[line.key]);
}

// Computed fields that only have values after calculateAllLinesTotals runs.
// If any expression references these, a 2nd pass is needed.
const COMPUTED_FIELD_MARKERS = ['unitPrice', 'totalUnitExcludingTax', 'totalExcludingTax', 'totalIncludingTax', 'unitPriceWithChildren'];

function expressionReferencesComputedFields(line: BudgetLineDto): boolean {
    const expressions = [
        line.quantityExpression, line.grossUnitPriceExpression,
        line.marginPercentageExpression, line.discountPercentageExpression,
        line.decennialInsurancePercentageExpression, line.unitPriceExpression,
        line.totalUnitExcludingTaxExpression, line.totalExcludingTaxExpression,
    ];
    // Also check parameter expressions
    const paramExpressions = parametersHaveExpressions(line.parameters) ? collectAllExpressionStrings(line.parameters) : [];
    const allExpressions = [...expressions.filter(Boolean), ...paramExpressions] as string[];
    if (allExpressions.length === 0) return false;
    return allExpressions.some(expr => COMPUTED_FIELD_MARKERS.some(field => expr.includes(field)));
}

function lineHasAnyExpression(line: BudgetLineDto): boolean {
    if (line.quantityExpression || line.grossUnitPriceExpression || line.marginPercentageExpression ||
        line.discountPercentageExpression || line.decennialInsurancePercentageExpression ||
        line.unitPriceExpression ||
        line.totalUnitExcludingTaxExpression || line.totalExcludingTaxExpression) {
        return true;
    }
    return parametersHaveExpressions(line.parameters);
}

/**
 * Recalculates expressions on all descendant lines of a given line.
 * Used when a parameter changes on a parent line - child lines may reference parent parameters.
 */
function recalculateDescendantExpressions(lines: BudgetLineDto[], parentLineKey: string): BudgetLineDto[] {
    // Pre-build parentKey → children index to collect descendants in O(n) instead of O(n × depth)
    const childrenByParent = new Map<string, BudgetLineDto[]>();
    for (const l of lines) {
        if (l.parentKey == null) continue;
        const arr = childrenByParent.get(l.parentKey);
        if (arr) arr.push(l);
        else childrenByParent.set(l.parentKey, [l]);
    }

    const descendantKeys = new Set<string>();
    const collectDescendants = (parentKey: string) => {
        const kids = childrenByParent.get(parentKey);
        if (!kids) return;
        for (const child of kids) {
            if (!descendantKeys.has(child.key)) {
                descendantKeys.add(child.key);
                collectDescendants(child.key);
            }
        }
    };
    collectDescendants(parentLineKey);

    if (descendantKeys.size === 0) return lines;

    // Clone into local array and index by key so updates are O(1) instead of O(n) per descendant
    const result = [...lines];
    const keyToIndex = new Map<string, number>();
    for (let i = 0; i < result.length; i++) keyToIndex.set(result[i].key, i);

    const descendantKeysArray = Array.from(descendantKeys);
    for (const descendantKey of descendantKeysArray) {
        const idx = keyToIndex.get(descendantKey);
        if (idx === undefined) continue;
        const line = result[idx];
        if (!line || !lineHasAnyExpression(line)) continue;

        // Recalculate line-level expressions
        const lineExpressionFields: { field: string; expression: string }[] = [];
        if (line.quantityExpression) lineExpressionFields.push({ field: 'quantity', expression: line.quantityExpression });
        if (line.grossUnitPriceExpression) lineExpressionFields.push({ field: 'grossUnitPrice', expression: line.grossUnitPriceExpression });
        if (line.marginPercentageExpression) lineExpressionFields.push({ field: 'marginPercentage', expression: line.marginPercentageExpression });
        if (line.discountPercentageExpression) lineExpressionFields.push({ field: 'discountPercentage', expression: line.discountPercentageExpression });
        if (line.totalUnitExcludingTaxExpression) lineExpressionFields.push({ field: 'totalUnitExcludingTax', expression: line.totalUnitExcludingTaxExpression });
        if (line.totalExcludingTaxExpression) lineExpressionFields.push({ field: 'totalExcludingTax', expression: line.totalExcludingTaxExpression });

        // Recalculate parameter expressions
        const recalcParams = parametersHaveExpressions(line.parameters)
            ? recalculateParameterExpressions(line.parameters, line, result)
            : line.parameters;

        const updatedLine = { ...line, parameters: recalcParams };

        // Sync quantity from QUANTITY-type parameters
        const hasQuantityParam = hasTotalParamQuantity(recalcParams);
        if (hasQuantityParam) {
            updatedLine.quantity = MathUtils.plus(...recalcParams.map(getTotalParamQuantity));
            updatedLine.quantityExpression = null;
        }

        // Evaluate line-level expressions
        if (lineExpressionFields.length > 0) {
            const expressionParams = createExpressionParametersForLine(updatedLine, result);
            const results = ExpressionSystem.evaluateBatch(lineExpressionFields.map(e => e.expression), expressionParams);
            for (let i = 0; i < lineExpressionFields.length; i++) {
                if (!results[i].error) {
                    (updatedLine as any)[lineExpressionFields[i].field] = results[i].value;
                }
            }
        }

        result[idx] = updatedLine;
    }

    return result;
}

function parametersHaveExpressions(parameters: BudgetLineParameterDto[]): boolean {
    for (const param of parameters) {
        if (param.number?.numberExpression || param.quantity?.quantityExpression ||
            param.price?.priceExpression || param.minMax?.valueExpression) {
            return true;
        }
        if (param.children && parametersHaveExpressions(param.children)) {
            return true;
        }
    }
    return false;
}

/**
 * Recalculates expressions for a single line only (used when a parameter changes).
 */
function updateSingleLineExpressionValues(stateLines: BudgetLineDto[], lineKey: string): BudgetLineDto[] {
    const lineIndex = stateLines.findIndex(l => l.key === lineKey);
    if (lineIndex === -1) return stateLines;

    const line = stateLines[lineIndex];
    if (!lineHasAnyExpression(line)) return stateLines;

    // Check if line has any line-level expressions (parameter expressions already handled by updateLineParameter)
    const hasLineExpressions = line.quantityExpression || line.grossUnitPriceExpression ||
        line.marginPercentageExpression || line.discountPercentageExpression ||
        line.totalUnitExcludingTaxExpression || line.totalExcludingTaxExpression;

    if (!hasLineExpressions) return stateLines;

    const expressionParams = createExpressionParametersForLine(line, stateLines);

    const lineExpressions: { field: string; expression: string }[] = [];
    if (line.quantityExpression) lineExpressions.push({ field: 'quantity', expression: line.quantityExpression });
    if (line.grossUnitPriceExpression) lineExpressions.push({ field: 'grossUnitPrice', expression: line.grossUnitPriceExpression });
    if (line.marginPercentageExpression) lineExpressions.push({ field: 'marginPercentage', expression: line.marginPercentageExpression });
    if (line.discountPercentageExpression) lineExpressions.push({ field: 'discountPercentage', expression: line.discountPercentageExpression });
    if (line.totalUnitExcludingTaxExpression) lineExpressions.push({ field: 'totalUnitExcludingTax', expression: line.totalUnitExcludingTaxExpression });
    if (line.totalExcludingTaxExpression) lineExpressions.push({ field: 'totalExcludingTax', expression: line.totalExcludingTaxExpression });

    if (lineExpressions.length === 0) return stateLines;

    const results = ExpressionSystem.evaluateBatch(lineExpressions.map(e => e.expression), expressionParams);
    const updatedLine = { ...line };
    for (let i = 0; i < lineExpressions.length; i++) {
        if (!results[i].error) {
            (updatedLine as any)[lineExpressions[i].field] = results[i].value;
        }
    }

    // Sync quantity from parameters if needed
    const hasQuantityParam = hasTotalParamQuantity(updatedLine.parameters);
    if (hasQuantityParam) {
        updatedLine.quantity = MathUtils.plus(...updatedLine.parameters.map(getTotalParamQuantity));
        updatedLine.quantityExpression = null;
    }

    const result = [...stateLines];
    result[lineIndex] = updatedLine;
    return result;
}

function updateLinesExpressionValues(stateLines: BudgetLineDto[], budgetData?: BudgetData): BudgetLineDto[] {
    // Check if any line has expressions at all - if not, skip everything
    const linesWithExpressions = stateLines.filter(lineHasAnyExpression);
    if (linesWithExpressions.length === 0) {
        return stateLines;
    }

    // PRE-COMPUTE: Build all line params once → O(n) instead of O(n²)
    const lineParamsCache = new Map<string, ExpressionParameter[]>();
    for (const line of stateLines) {
        lineParamsCache.set(line.key, createExpressionParametersForSingleLine(line, line.key));
    }
    const budgetParams = budgetData ? createBudgetExpressionParameters(budgetData) : [];

    // Process lines iteratively so that each line sees the already-updated values
    // from previously processed lines (handles dependency chains like A → B → C)
    const currentLines = [...stateLines];

    for (let i = 0; i < currentLines.length; i++) {
        const line = currentLines[i];

        // Skip lines that have no expressions at all
        if (!lineHasAnyExpression(line)) {
            continue;
        }

        // ASSEMBLE per-line params from pre-computed cache
        const expressionParams = assembleExpressionParamsFromCache(line, currentLines, lineParamsCache, budgetParams);

        // Collect all line-level expressions for batch evaluation
        const lineExpressions: { field: string; expression: string }[] = [];
        if (line.quantityExpression) lineExpressions.push({ field: 'quantity', expression: line.quantityExpression });
        if (line.grossUnitPriceExpression) lineExpressions.push({ field: 'grossUnitPrice', expression: line.grossUnitPriceExpression });
        if (line.marginPercentageExpression) lineExpressions.push({ field: 'marginPercentage', expression: line.marginPercentageExpression });
        if (line.discountPercentageExpression) lineExpressions.push({ field: 'discountPercentage', expression: line.discountPercentageExpression });
        if (line.decennialInsurancePercentageExpression) lineExpressions.push({ field: 'decennialInsurancePercentage', expression: line.decennialInsurancePercentageExpression });
        if (line.unitPriceExpression) lineExpressions.push({ field: 'unitPrice', expression: line.unitPriceExpression });
        if (line.totalUnitExcludingTaxExpression) lineExpressions.push({ field: 'totalUnitExcludingTax', expression: line.totalUnitExcludingTaxExpression });
        if (line.totalExcludingTaxExpression) lineExpressions.push({ field: 'totalExcludingTax', expression: line.totalExcludingTaxExpression });

        // Recalculate parameter expressions FIRST so line-level expressions see updated values
        const updatedLine = { ...line };
        const updatedParameters = recalculateParameterExpressions(updatedLine.parameters, updatedLine, currentLines, undefined, lineParamsCache);
        updatedLine.parameters = updatedParameters;

        // Sync line quantity from QUANTITY-type parameters (their values may have been updated by expressions)
        const hasQuantityParam = hasTotalParamQuantity(updatedParameters);
        if (hasQuantityParam) {
            updatedLine.quantity = MathUtils.plus(...updatedParameters.map(getTotalParamQuantity));
            updatedLine.quantityExpression = null;
        }

        // Rebuild expression params with updated parameter values
        const updatedExpressionParams = assembleExpressionParamsFromCache(updatedLine, currentLines, lineParamsCache, budgetParams);

        // Batch evaluate all line-level expressions with fresh parameter values
        if (lineExpressions.length > 0) {
            const results = ExpressionSystem.evaluateBatch(lineExpressions.map(e => e.expression), updatedExpressionParams);
            for (let j = 0; j < lineExpressions.length; j++) {
                if (!results[j].error) {
                    (updatedLine as any)[lineExpressions[j].field] = results[j].value;
                }
            }
        }

        // Update the line in currentLines so subsequent lines see the new values
        currentLines[i] = updatedLine;

        // UPDATE cache so subsequent lines see the new values
        lineParamsCache.set(updatedLine.key, createExpressionParametersForSingleLine(updatedLine, updatedLine.key));
    }

    return currentLines;
}

/**
 * Creates expression parameters for evaluating formulas within a line's parameters.
 * Includes parameters from ALL lines so expressions can reference other lines.
 * Similar to getExpressionParameters in utils.ts but simplified for internal use.
 */

const ANCESTOR_FIELD_KEYS_REDUCER = [
    'quantity', 'grossUnitPrice', 'marginPercentage', 'discountPercentage',
    'decennialInsurancePercentage',
    'unitPrice', 'totalUnitExcludingTax', 'unitPriceWithChildren',
    'totalExcludingTax', 'totalIncludingTax', 'unitSymbol',
];

function getLineFieldValueForAncestor(line: BudgetLineDto, fieldKey: string): number | string | null {
    switch (fieldKey) {
        case 'quantity': return line.quantity;
        case 'grossUnitPrice': return line.grossUnitPrice;
        case 'marginPercentage': return line.marginPercentage;
        case 'discountPercentage': return line.discountPercentage;
        case 'decennialInsurancePercentage': return line.decennialInsurancePercentage;
        case 'unitPrice': return line.unitPrice;
        case 'totalUnitExcludingTax': return line.totalUnitExcludingTax;
        case 'unitPriceWithChildren': return line.unitPriceWithChildren;
        case 'totalExcludingTax': return line.totalExcludingTax;
        case 'totalIncludingTax': return line.totalIncludingTax;
        case 'unitSymbol': return line.unitSymbol ?? '';
        default: return 0;
    }
}

function resolveAncestorFieldValueReducer(
    currentLine: BudgetLineDto,
    allLines: BudgetLineDto[],
    fieldKey: string
): number | string {
    // Collect all ancestors from current line to root (bottom-up)
    const ancestors: BudgetLineDto[] = [];
    let parentKey = currentLine.parentKey;
    while (parentKey) {
        const parentLine = findLineByKey(parentKey, allLines);
        if (!parentLine) break;
        ancestors.push(parentLine);
        parentKey = parentLine.parentKey;
    }

    // Reverse to get top-down order (root first), then find the first with a filled value
    ancestors.reverse();
    for (const ancestor of ancestors) {
        const value = getLineFieldValueForAncestor(ancestor, fieldKey);
        if (fieldKey === 'unitSymbol') {
            if (value && value !== '') return value;
        } else {
            if (value !== 0 && value !== null && value !== undefined) return value;
        }
    }

    return fieldKey === 'unitSymbol' ? '' : 0;
}

// Helper to create an ExpressionParameter with pre-serialized ID (avoids repeated regex in buildVariableMap)
function makeExprParam(id: string, name: string, value: string | number | number[] | null): ExpressionParameter {
    return { id, serializedId: ExpressionSystem.serializeParameterMentionId(id), name, value };
}

function createBudgetExpressionParameters(budgetData: BudgetData): ExpressionParameter[] {
    const budgetKey = 'budget';
    return [
        makeExprParam(ExpressionSystem.formatParameterId([budgetKey, 'defaultDiscountPercentage']), '', budgetData.defaultDiscountPercentage ?? 0),
        makeExprParam(ExpressionSystem.formatParameterId([budgetKey, 'defaultMarginPercentage']), '', budgetData.defaultMarginPercentage ?? 0),
        makeExprParam(ExpressionSystem.formatParameterId([budgetKey, 'defaultCommissionMarginPercentage']), '', budgetData.defaultCommissionMarginPercentage ?? 0),
        makeExprParam(ExpressionSystem.formatParameterId([budgetKey, 'defaultCombinedMarginPercentage']), '', (budgetData.defaultMarginPercentage ?? 0) + (budgetData.defaultCommissionMarginPercentage ?? 0)),
        makeExprParam(ExpressionSystem.formatParameterId([budgetKey, 'defaultDecennialInsurancePercentage']), '', budgetData.defaultDecennialInsurancePercentage ?? 0),
        makeExprParam(ExpressionSystem.formatParameterId([budgetKey, 'defaultTaxValue']), '', budgetData.defaultTaxValue ?? 0),
        // Expressions reference the lines-only totals (generalities excluded) so a generality
        // expression that references the budget total doesn't become self-referential.
        makeExprParam(ExpressionSystem.formatParameterId([budgetKey, 'totalNetExcludingTax']), '', budgetData.linesTotalNetExcludingTax ?? budgetData.totalNetExcludingTax),
        makeExprParam(ExpressionSystem.formatParameterId([budgetKey, 'discount']), '', budgetData.discount),
        makeExprParam(ExpressionSystem.formatParameterId([budgetKey, 'totalExcludingTax']), '', budgetData.linesTotalExcludingTax ?? budgetData.totalExcludingTax),
        makeExprParam(ExpressionSystem.formatParameterId([budgetKey, 'totalTax']), '', budgetData.linesTotalTax ?? budgetData.totalTax),
        makeExprParam(ExpressionSystem.formatParameterId([budgetKey, 'totalIncludingTax']), '', budgetData.linesTotalIncludingTax ?? budgetData.totalIncludingTax),
    ];
}

function findNearestPricedAncestor(line: BudgetLineDto, allLines: BudgetLineDto[]): BudgetLineDto | null {
    let p: BudgetLineDto | null = line.parentKey ? (allLines.find(l => l.key === line.parentKey) ?? null) : null;
    while (p && (p.lineType === BudgetLineType.SECTION || p.lineType === BudgetLineType.PAGE_BREAK || p.lineType === BudgetLineType.NOTE)) {
        p = p.parentKey ? (allLines.find(l => l.key === p!.parentKey) ?? null) : null;
    }
    return p;
}

function assembleExpressionParamsFromCache(
    currentLine: BudgetLineDto,
    allLines: BudgetLineDto[],
    lineParamsCache: Map<string, ExpressionParameter[]>,
    budgetParams: ExpressionParameter[]
): ExpressionParameter[] {
    const result: ExpressionParameter[] = [...budgetParams];

    const parentLine = findNearestPricedAncestor(currentLine, allLines);

    for (const line of allLines) {
        if (line.key === currentLine.key) {
            // Add as CURRENT_LINE using the current line's cached params re-keyed
            result.push(...createExpressionParametersForSingleLine(currentLine, 'CURRENT_LINE'));
        } else if (parentLine && line.key === parentLine.key) {
            // Add as CURRENT_PARENT_LINE
            result.push(...createExpressionParametersForSingleLine(line, 'CURRENT_PARENT_LINE'));
            // Also register with original key
            const cached = lineParamsCache.get(line.key);
            if (cached) result.push(...cached);
        } else {
            const cached = lineParamsCache.get(line.key);
            if (cached) result.push(...cached);
        }
    }

    // Add ANCESTOR_LINE parameters
    for (const fieldKey of ANCESTOR_FIELD_KEYS_REDUCER) {
        const value = resolveAncestorFieldValueReducer(currentLine, allLines, fieldKey);
        result.push(makeExprParam(ExpressionSystem.formatParameterId(['ANCESTOR_LINE', fieldKey]), '', value));
    }

    // Add composition (PRODUCTS) section totals for CURRENT_LINE
    try {
        const compositionChildSections = allLines.filter(l => l.parentKey === currentLine.key && l.lineType === BudgetLineType.SECTION);
        for (const section of compositionChildSections) {
            let strippedId: string | null = null;
            if (section.sourceFamilyParameterId) {
                strippedId = section.sourceFamilyParameterId.replace(/[^0-9A-Za-z_]/g, '');
            } else if (currentLine.parameters && currentLine.parameters.length > 0) {
                // Fallback: match by designation against PRODUCTS parameters
                const findProductsParam = (params: BudgetLineParameterDto[]): BudgetLineParameterDto | undefined => {
                    for (const p of params) {
                        if (p.parameterType === ParameterSectionParameterType.PRODUCTS && p.parameterName === section.designation && p.familyParameterId) {
                            return p;
                        }
                        if (p.children && p.children.length > 0) {
                            const found = findProductsParam(p.children);
                            if (found) return found;
                        }
                    }
                    return undefined;
                };
                const matchedParam = findProductsParam(currentLine.parameters);
                if (matchedParam?.familyParameterId) {
                    strippedId = matchedParam.familyParameterId.replace(/[^0-9A-Za-z_]/g, '');
                }
            }
            if (!strippedId) continue;

            const compositionFields: { field: string; value: number }[] = [
                { field: 'totalExcludingTax', value: section.totalExcludingTax ?? 0 },
                { field: 'totalIncludingTax', value: section.totalIncludingTax ?? 0 },
            ];
            for (const { field, value } of compositionFields) {
                result.push(makeExprParam(ExpressionSystem.formatParameterId(['CURRENT_LINE', strippedId, 'composition', field]), '', value));
                result.push(makeExprParam(ExpressionSystem.formatParameterId([strippedId, 'composition', field]), '', value));
            }
        }

        // Add sum of ALL composition (PRODUCTS) sections for CURRENT_LINE
        let compositionSumExcl = 0;
        let compositionSumIncl = 0;
        for (const section of compositionChildSections) {
            compositionSumExcl = MathUtils.plus(compositionSumExcl, section.totalExcludingTax ?? 0);
            compositionSumIncl = MathUtils.plus(compositionSumIncl, section.totalIncludingTax ?? 0);
        }
        result.push(makeExprParam(ExpressionSystem.formatParameterId(['CURRENT_LINE', 'compositionSumTotalExcludingTax']), '', compositionSumExcl));
        result.push(makeExprParam(ExpressionSystem.formatParameterId(['CURRENT_LINE', 'compositionSumTotalIncludingTax']), '', compositionSumIncl));
    } catch (e) {
        console.error('Error building composition expression params (assembleExpressionParamsFromCache):', e);
    }

    return result;
}

function createExpressionParametersForLine(currentLine: BudgetLineDto, allLines: BudgetLineDto[], budgetData?: BudgetData): ExpressionParameter[] {
    const budgetParams = budgetData ? createBudgetExpressionParameters(budgetData) : [];
    // Build cache for all lines
    const lineParamsCache = new Map<string, ExpressionParameter[]>();
    for (const line of allLines) {
        lineParamsCache.set(line.key, createExpressionParametersForSingleLine(line, line.key));
    }
    return assembleExpressionParamsFromCache(currentLine, allLines, lineParamsCache, budgetParams);
}

/**
 * Creates expression parameters for a single line (used to cache other lines' params).
 */
function createExpressionParametersForSingleLine(line: BudgetLineDto, lineKey: string): ExpressionParameter[] {
    const result: ExpressionParameter[] = [];
    const idsPrefix = [lineKey];

    result.push(makeExprParam(ExpressionSystem.formatParameterId([...idsPrefix, 'quantity']), '', line.quantity));
    result.push(makeExprParam(ExpressionSystem.formatParameterId([...idsPrefix, 'grossUnitPrice']), '', line.grossUnitPrice));
    result.push(makeExprParam(ExpressionSystem.formatParameterId([...idsPrefix, 'totalCostUnitPrice']), '', MathUtils.plus(line.grossUnitPrice, MathUtils.multiply(line.grossUnitPrice, MathUtils.divide(line.discountPercentage, 100)) * -1, line.unitPriceWithChildren)));
    result.push(makeExprParam(ExpressionSystem.formatParameterId([...idsPrefix, 'marginPercentage']), '', line.marginPercentage));
    result.push(makeExprParam(ExpressionSystem.formatParameterId([...idsPrefix, 'discountPercentage']), '', line.discountPercentage));
    result.push(makeExprParam(ExpressionSystem.formatParameterId([...idsPrefix, 'decennialInsurancePercentage']), '', line.decennialInsurancePercentage));
    result.push(makeExprParam(ExpressionSystem.formatParameterId([...idsPrefix, 'unitPrice']), '', line.unitPrice));
    result.push(makeExprParam(ExpressionSystem.formatParameterId([...idsPrefix, 'totalUnitExcludingTax']), '', line.totalUnitExcludingTax));
    result.push(makeExprParam(ExpressionSystem.formatParameterId([...idsPrefix, 'unitPriceWithChildren']), '', line.unitPriceWithChildren));
    const totalCostUnitPriceParam = MathUtils.plus(line.grossUnitPrice, MathUtils.multiply(line.grossUnitPrice, MathUtils.divide(line.discountPercentage, 100)) * -1, line.unitPriceWithChildren);
    const netProfitParam = MathUtils.multiply(MathUtils.minus(line.unitPrice, totalCostUnitPriceParam), line.quantity ?? 0);
    result.push(makeExprParam(ExpressionSystem.formatParameterId([...idsPrefix, 'netProfit']), '', netProfitParam));
    result.push(makeExprParam(ExpressionSystem.formatParameterId([...idsPrefix, 'netProfitPercentage']), '', line.totalExcludingTax !== 0 ? MathUtils.multiply(MathUtils.divide(netProfitParam, line.totalExcludingTax), 100) : 0));
    result.push(makeExprParam(ExpressionSystem.formatParameterId([...idsPrefix, 'totalExcludingTax']), '', line.totalExcludingTax));
    result.push(makeExprParam(ExpressionSystem.formatParameterId([...idsPrefix, 'totalIncludingTax']), '', line.totalIncludingTax));
    result.push(makeExprParam(ExpressionSystem.formatParameterId([...idsPrefix, 'unitSymbol']), '', line.unitSymbol ?? ''));

    if (line.parameters && line.parameters.length > 0) {
        const addParams = (parameters: BudgetLineParameterDto[], ids: string[], names: string[]) => {
            for (const parameter of parameters) {
                if (parameter.type === ParameterSectionType.PARAMETER) {
                    switch (parameter.parameterType) {
                        case ParameterSectionParameterType.PRODUCTS:
                        case ParameterSectionParameterType.IMAGES:
                        case ParameterSectionParameterType.TEXT:
                            break;
                        case ParameterSectionParameterType.NUMBER_MIN_MAX:
                            result.push(makeExprParam(ExpressionSystem.formatParameterId([...ids, parameter.familyParameterId ?? '', 'parameter']), ExpressionSystem.formatParameterName([...names, parameter.parameterName ?? '']), parameter.minMax?.value ?? 0));
                            break;
                        case ParameterSectionParameterType.NUMBER:
                            result.push(makeExprParam(ExpressionSystem.formatParameterId([...ids, parameter.familyParameterId ?? '', 'parameter']), ExpressionSystem.formatParameterName([...names, parameter.parameterName ?? '']), parameter.number?.number ?? 0));
                            break;
                        case ParameterSectionParameterType.QUANTITY:
                            result.push(makeExprParam(ExpressionSystem.formatParameterId([...ids, parameter.familyParameterId ?? '', 'parameter']), ExpressionSystem.formatParameterName([...names, parameter.parameterName ?? '']), parameter.quantity?.quantity ?? 0));
                            break;
                        case ParameterSectionParameterType.PRICE:
                            result.push(makeExprParam(ExpressionSystem.formatParameterId([...ids, parameter.familyParameterId ?? '', 'parameter']), ExpressionSystem.formatParameterName([...names, parameter.parameterName ?? '']), parameter.price?.price ?? 0));
                            break;
                        case ParameterSectionParameterType.WEIGHT:
                            result.push(makeExprParam(ExpressionSystem.formatParameterId([...ids, parameter.familyParameterId ?? '', 'parameter']), ExpressionSystem.formatParameterName([...names, parameter.parameterName ?? '']), parameter.number?.number ?? 0));
                            break;
                    }

                    // Also register catalog parameter ID so formulas from catalog resolve correctly
                    if (parameter.catalogParameterId) {
                        const catalogVal = parameter.parameterType === ParameterSectionParameterType.NUMBER_MIN_MAX
                            ? (parameter.minMax?.value ?? 0)
                            : parameter.parameterType === ParameterSectionParameterType.QUANTITY
                                ? (parameter.quantity?.quantity ?? 0)
                                : parameter.parameterType === ParameterSectionParameterType.PRICE
                                    ? (parameter.price?.price ?? 0)
                                    : (parameter.number?.number ?? 0);

                        result.push(makeExprParam(
                            ExpressionSystem.formatParameterId([...ids, parameter.catalogParameterId, 'parameter']),
                            ExpressionSystem.formatParameterName([...names, parameter.parameterName ?? '']),
                            catalogVal
                        ));
                    }

                    // Additionally register catalog-based parameter reference for parent line resolution
                    if (idsPrefix[0] === 'CURRENT_PARENT_LINE') {
                        let resolvedCatalogId = parameter.catalogParameterId;
                        if (!resolvedCatalogId && parameter.parameterName) {
                            const catalogCache = getCatalogParametersCache();
                            if (catalogCache) {
                                const normalizedName = parameter.parameterName.trim().toLowerCase();
                                const matchingCatalog = catalogCache.find(cp => cp.name.trim().toLowerCase() === normalizedName);
                                if (matchingCatalog) {
                                    resolvedCatalogId = matchingCatalog.id;
                                }
                            }
                        }

                        if (resolvedCatalogId) {
                            const paramValue = parameter.parameterType === ParameterSectionParameterType.NUMBER_MIN_MAX
                                ? (parameter.minMax?.value ?? 0)
                                : parameter.parameterType === ParameterSectionParameterType.QUANTITY
                                    ? (parameter.quantity?.quantity ?? 0)
                                    : parameter.parameterType === ParameterSectionParameterType.PRICE
                                        ? (parameter.price?.price ?? 0)
                                        : (parameter.number?.number ?? 0);

                            result.push(makeExprParam(
                                ExpressionSystem.formatParameterId([...ids, 'CATALOG', resolvedCatalogId, 'parameter']),
                                ExpressionSystem.formatParameterName([...names, parameter.parameterName ?? '']),
                                paramValue
                            ));
                        }
                    }
                } else {
                    addParams(parameter.children ?? [], [...ids], [...names, parameter.title ?? '']);
                }
            }
        };
        addParams(line.parameters, [...idsPrefix], []);
    }

    return result;
}

/**
 * Recalculates parameter values that have formula expressions.
 * Uses multiple passes to handle dependencies between formulas.
 */
/**
 * Collects all expression strings from parameters (for dependency checking).
 */
function collectAllExpressionStrings(parameters: BudgetLineParameterDto[]): string[] {
    const result: string[] = [];
    for (const p of parameters) {
        if (p.number?.numberExpression) result.push(p.number.numberExpression);
        if (p.quantity?.quantityExpression) result.push(p.quantity.quantityExpression);
        if (p.price?.priceExpression) result.push(p.price.priceExpression);
        if (p.minMax?.valueExpression) result.push(p.minMax.valueExpression);
        if (p.children) result.push(...collectAllExpressionStrings(p.children));
    }
    return result;
}

function recalculateParameterExpressions(
    parameters: BudgetLineParameterDto[],
    line: BudgetLineDto,
    allLines: BudgetLineDto[],
    changedFamilyParameterId?: string,
    lineParamsCache?: Map<string, ExpressionParameter[]>
): BudgetLineParameterDto[] {
    // Short-circuit: if no parameter has an expression, skip entirely
    if (!parametersHaveExpressions(parameters)) {
        return parameters;
    }

    // If we know which parameter changed, check if any expression references it.
    // If not, no formula depends on this parameter so we can skip recalculation.
    if (changedFamilyParameterId) {
        const mentionId = ExpressionSystem.serializeParameterMentionId(
            ExpressionSystem.formatParameterId(['CURRENT_LINE', changedFamilyParameterId, 'parameter'])
        );
        const allExpressions = collectAllExpressionStrings(parameters);
        const isReferenced = allExpressions.some(expr => expr.includes(mentionId));
        if (!isReferenced) {
            return parameters;
        }
    }

    const MAX_ITERATIONS = 3; // Most formulas stabilize in 1-2 passes
    let currentParameters = parameters;
    let previousHash = '';

    // Pre-compute expression params for other lines (they don't change between iterations)
    const otherLinesParams: ExpressionParameter[] = [];
    const parentLine = findNearestPricedAncestor(line, allLines);

    if (lineParamsCache) {
        // Use pre-computed cache — O(1) lookup per line instead of rebuilding
        for (const otherLine of allLines) {
            if (otherLine.key === line.key) continue;
            if (parentLine && otherLine.key === parentLine.key) {
                otherLinesParams.push(...createExpressionParametersForSingleLine(otherLine, 'CURRENT_PARENT_LINE'));
                const cached = lineParamsCache.get(otherLine.key);
                if (cached) otherLinesParams.push(...cached);
            } else {
                const cached = lineParamsCache.get(otherLine.key);
                if (cached) otherLinesParams.push(...cached);
            }
        }
    } else {
        // Fallback: build from scratch (used when called without cache)
        for (const otherLine of allLines) {
            if (otherLine.key === line.key) continue;
            const lineKey = (parentLine && otherLine.key === parentLine.key) ? 'CURRENT_PARENT_LINE' : otherLine.key;
            otherLinesParams.push(...createExpressionParametersForSingleLine(otherLine, lineKey));
            if (parentLine && otherLine.key === parentLine.key) {
                otherLinesParams.push(...createExpressionParametersForSingleLine(otherLine, otherLine.key));
            }
        }
    }

    // Add ANCESTOR_LINE parameters (walk up parent chain for nearest non-zero/non-empty value)
    for (const fieldKey of ANCESTOR_FIELD_KEYS_REDUCER) {
        const value = resolveAncestorFieldValueReducer(line, allLines, fieldKey);
        otherLinesParams.push(makeExprParam(ExpressionSystem.formatParameterId(['ANCESTOR_LINE', fieldKey]), '', value));
    }

    // Add catalog parameter references as fallback (ensures CURRENT_PARENT_LINE__CATALOG__ tokens resolve)
    const catalogCache = getCatalogParametersCache();
    if (catalogCache) {
        const numericTypes = ['NUMBER', 'QUANTITY', 'PRICE', 'NUMBER_MIN_MAX', 'WEIGHT'];
        const existingIds = new Set(otherLinesParams.map(p => p.id));
        for (const cp of catalogCache) {
            if (cp.parameterType && numericTypes.includes(cp.parameterType)) {
                const catalogId = ExpressionSystem.formatParameterId(['CURRENT_PARENT_LINE', 'CATALOG', cp.id, 'parameter']);
                if (!existingIds.has(catalogId)) {
                    otherLinesParams.push(makeExprParam(catalogId, '', 0));
                }
            }
        }
    }

    // Add composition (PRODUCTS) section totals for CURRENT_LINE
    try {
        const compositionSectionsForParams = allLines.filter(l => l.parentKey === line.key && l.lineType === BudgetLineType.SECTION);
        for (const section of compositionSectionsForParams) {
            let strippedId: string | null = null;
            if (section.sourceFamilyParameterId) {
                strippedId = section.sourceFamilyParameterId.replace(/[^0-9A-Za-z_]/g, '');
            } else if (line.parameters && line.parameters.length > 0) {
                const findProductsParam = (params: BudgetLineParameterDto[]): BudgetLineParameterDto | undefined => {
                    for (const p of params) {
                        if (p.parameterType === ParameterSectionParameterType.PRODUCTS && p.parameterName === section.designation && p.familyParameterId) {
                            return p;
                        }
                        if (p.children && p.children.length > 0) {
                            const found = findProductsParam(p.children);
                            if (found) return found;
                        }
                    }
                    return undefined;
                };
                const matchedParam = findProductsParam(line.parameters);
                if (matchedParam?.familyParameterId) {
                    strippedId = matchedParam.familyParameterId.replace(/[^0-9A-Za-z_]/g, '');
                }
            }
            if (!strippedId) continue;

            const compFields: { field: string; value: number }[] = [
                { field: 'totalExcludingTax', value: section.totalExcludingTax ?? 0 },
                { field: 'totalIncludingTax', value: section.totalIncludingTax ?? 0 },
            ];
            for (const { field, value } of compFields) {
                otherLinesParams.push(makeExprParam(ExpressionSystem.formatParameterId(['CURRENT_LINE', strippedId, 'composition', field]), '', value));
                otherLinesParams.push(makeExprParam(ExpressionSystem.formatParameterId([strippedId, 'composition', field]), '', value));
            }
        }
    } catch (e) {
        console.error('Error building composition expression params (recalculateParameterExpressions):', e);
    }

    // Multiple passes to handle formula dependencies (e.g., A uses B, B uses C)
    for (let iteration = 0; iteration < MAX_ITERATIONS; iteration++) {
        // Only rebuild expression params for the current line
        const currentLineParams = createExpressionParametersForSingleLine({ ...line, parameters: currentParameters }, 'CURRENT_LINE');
        const expressionParams = [...otherLinesParams, ...currentLineParams];

        // Recalculate all parameters using batch evaluation (single HyperFormula instance)
        const newParameters = recalculateParametersOnce(currentParameters, expressionParams);

        // Check if values have stabilized
        const newHash = hashParameterValues(newParameters);
        if (newHash === previousHash) {
            return newParameters;
        }

        previousHash = newHash;
        currentParameters = newParameters;
    }

    return currentParameters;
}

/**
 * Creates a simple hash of parameter values to detect when recalculation has stabilized.
 */
function hashParameterValues(parameters: BudgetLineParameterDto[]): string {
    const values: number[] = [];

    function collectValues(params: BudgetLineParameterDto[]) {
        for (const p of params) {
            values.push(p.number?.number ?? 0);
            values.push(p.quantity?.quantity ?? 0);
            values.push(p.price?.price ?? 0);
            values.push(p.minMax?.value ?? 0);
            if (p.children) {
                collectValues(p.children);
            }
        }
    }

    collectValues(parameters);
    return values.join(',');
}

/**
 * Collects all expressions from parameters into a flat list for batch evaluation.
 */
interface ExpressionEntry {
    path: number[]; // index path to locate the parameter in the tree
    field: 'number' | 'quantity' | 'price' | 'minMax';
    expression: string;
}

function collectExpressions(parameters: BudgetLineParameterDto[], path: number[] = []): ExpressionEntry[] {
    const entries: ExpressionEntry[] = [];
    for (let i = 0; i < parameters.length; i++) {
        const p = parameters[i];
        const currentPath = [...path, i];
        if (p.number?.numberExpression) entries.push({ path: currentPath, field: 'number', expression: p.number.numberExpression });
        if (p.quantity?.quantityExpression) entries.push({ path: currentPath, field: 'quantity', expression: p.quantity.quantityExpression });
        if (p.price?.priceExpression) entries.push({ path: currentPath, field: 'price', expression: p.price.priceExpression });
        if (p.minMax?.valueExpression) entries.push({ path: currentPath, field: 'minMax', expression: p.minMax.valueExpression });
        if (p.children && p.children.length > 0) {
            entries.push(...collectExpressions(p.children, currentPath));
        }
    }
    return entries;
}

/**
 * Single pass of recalculating parameter expressions using batch evaluation.
 */
function recalculateParametersOnce(
    parameters: BudgetLineParameterDto[],
    expressionParams: ExpressionParameter[]
): BudgetLineParameterDto[] {
    const entries = collectExpressions(parameters);
    if (entries.length === 0) return parameters;

    // Batch evaluate all expressions at once (single HyperFormula instance)
    const results = ExpressionSystem.evaluateBatch(entries.map(e => e.expression), expressionParams);

    // Deep clone parameters for mutation
    function cloneParams(params: BudgetLineParameterDto[]): BudgetLineParameterDto[] {
        return params.map(p => ({
            ...p,
            number: p.number ? { ...p.number } : p.number,
            quantity: p.quantity ? { ...p.quantity } : p.quantity,
            price: p.price ? { ...p.price } : p.price,
            minMax: p.minMax ? { ...p.minMax } : p.minMax,
            children: p.children ? cloneParams(p.children) : p.children,
        }));
    }

    const result = cloneParams(parameters);

    // Apply results by path
    for (let i = 0; i < entries.length; i++) {
        if (results[i].error) continue;
        const entry = entries[i];
        let params = result;
        for (let j = 0; j < entry.path.length - 1; j++) {
            params = params[entry.path[j]].children!;
        }
        const param = params[entry.path[entry.path.length - 1]];

        switch (entry.field) {
            case 'number':
                param.number = { ...param.number, number: results[i].value };
                break;
            case 'quantity':
                param.quantity = { ...param.quantity, quantity: results[i].value };
                break;
            case 'price':
                param.price = { ...param.price, price: results[i].value };
                break;
            case 'minMax':
                param.minMax = { ...param.minMax, value: results[i].value };
                break;
        }
    }

    return result;
}

function findParameterByKey(parameters: BudgetLineParameterDto[], key: string): BudgetLineParameterDto | undefined {
    for (const p of parameters) {
        if (p.key === key) return p;
        if (p.children) {
            const found = findParameterByKey(p.children, key);
            if (found) return found;
        }
    }
    return undefined;
}

const FORMULA_AFFECTING_TYPES: string[] = [
    ParameterSectionParameterType.NUMBER,
    ParameterSectionParameterType.QUANTITY,
    ParameterSectionParameterType.PRICE,
    ParameterSectionParameterType.NUMBER_MIN_MAX,
    ParameterSectionParameterType.WEIGHT,
];

/**
 * Light update: just set the parameter value in the tree, no recalculation.
 */
function updateLineParameterOnly(lines: BudgetLineDto[], lineKey: string, updatedParameter: AtLeast<BudgetLineParameterDto, 'key'>) {
    const line = findLineByKey(lineKey, lines);
    if (!line) {
        return lines;
    }
    const parameters = updateLineParameters(line.parameters, updatedParameter);
    return updateLine(lines, { key: lineKey, parameters, changedParameters: true });
}

/**
 * Checks if a parameter change requires any recalculation.
 * Returns true if any expression (parameter or line-level) references the changed parameter.
 */
function parameterChangeNeedsRecalculation(line: BudgetLineDto, changedFamilyParameterId: string | undefined): boolean {
    if (!changedFamilyParameterId) return true;

    // Build mention ID for the changed parameter
    const mentionId = ExpressionSystem.serializeParameterMentionId(
        ExpressionSystem.formatParameterId(['CURRENT_LINE', changedFamilyParameterId, 'parameter'])
    );

    // Check parameter expressions
    const paramExpressions = collectAllExpressionStrings(line.parameters);
    if (paramExpressions.some(expr => expr.includes(mentionId))) return true;

    // Check line-level expressions
    const lineExpressions = [
        line.quantityExpression, line.grossUnitPriceExpression,
        line.marginPercentageExpression, line.discountPercentageExpression,
        line.totalUnitExcludingTaxExpression, line.totalExcludingTaxExpression
    ].filter(Boolean) as string[];
    if (lineExpressions.some(expr => expr.includes(mentionId))) return true;

    return false;
}

/**
 * Full recalculation after a parameter change:
 * 1. Recalculate parameter expressions (dependency-aware, iterative)
 * 2. Recalculate line-level expressions (quantity, price, etc.)
 * 3. Sync line quantity from QUANTITY-type parameters
 */
function recalculateLineAfterParameterChange(lines: BudgetLineDto[], lineKey: string) {
    const line = findLineByKey(lineKey, lines);
    if (!line) {
        return lines;
    }

    // Step 1: Recalculate parameter expressions
    const recalculatedParameters = recalculateParameterExpressions(line.parameters, line, lines);

    // Step 2: Build updated line for line-level expression recalculation
    const updatedLine = { ...line, parameters: recalculatedParameters };

    // Sync line quantity from QUANTITY-type parameters
    const hasQuantityParam = hasTotalParamQuantity(recalculatedParameters);
    if (hasQuantityParam) {
        updatedLine.quantity = MathUtils.plus(...recalculatedParameters.map(getTotalParamQuantity));
        updatedLine.quantityExpression = null;
    }

    // Step 3: Recalculate line-level expressions if any exist
    const lineExpressionFields: { field: string; expression: string }[] = [];
    if (updatedLine.quantityExpression) lineExpressionFields.push({ field: 'quantity', expression: updatedLine.quantityExpression });
    if (updatedLine.grossUnitPriceExpression) lineExpressionFields.push({ field: 'grossUnitPrice', expression: updatedLine.grossUnitPriceExpression });
    if (updatedLine.marginPercentageExpression) lineExpressionFields.push({ field: 'marginPercentage', expression: updatedLine.marginPercentageExpression });
    if (updatedLine.discountPercentageExpression) lineExpressionFields.push({ field: 'discountPercentage', expression: updatedLine.discountPercentageExpression });
    if (updatedLine.decennialInsurancePercentageExpression) lineExpressionFields.push({ field: 'decennialInsurancePercentage', expression: updatedLine.decennialInsurancePercentageExpression });
    if (updatedLine.totalUnitExcludingTaxExpression) lineExpressionFields.push({ field: 'totalUnitExcludingTax', expression: updatedLine.totalUnitExcludingTaxExpression });
    if (updatedLine.totalExcludingTaxExpression) lineExpressionFields.push({ field: 'totalExcludingTax', expression: updatedLine.totalExcludingTaxExpression });

    if (lineExpressionFields.length > 0) {
        const expressionParams = createExpressionParametersForLine(updatedLine, lines);
        const results = ExpressionSystem.evaluateBatch(lineExpressionFields.map(e => e.expression), expressionParams);
        for (let i = 0; i < lineExpressionFields.length; i++) {
            if (!results[i].error) {
                (updatedLine as any)[lineExpressionFields[i].field] = results[i].value;
            }
        }
    }

    updatedLine.changedParameters = true;
    return updateLine(lines, updatedLine);
}

function updateLineParameter(lines: BudgetLineDto[], lineKey: string, updatedParameter: AtLeast<BudgetLineParameterDto, 'key'>) {
    const line = findLineByKey(lineKey, lines);
    if (!line) {
        return lines;
    }

    const parameters = updateLineParameters(line.parameters, updatedParameter);

    // Only recalculate formulas if the changed parameter can affect them (skip for TEXT, IMAGES, SUPPLIER, DATE)
    const fullParam = updatedParameter.key ? findParameterByKey(parameters, updatedParameter.key) : undefined;
    const canAffectFormulas = fullParam && FORMULA_AFFECTING_TYPES.includes(fullParam.parameterType ?? '');

    const recalculatedParameters = canAffectFormulas
        ? recalculateParameterExpressions(parameters, { ...line, parameters }, lines, fullParam?.familyParameterId ?? undefined)
        : parameters;

    // Sync line quantity from QUANTITY-type parameters
    const lineUpdate: AtLeast<BudgetLineDto, 'key'> = { key: lineKey, parameters: recalculatedParameters, changedParameters: true };
    const hasQuantityParam = hasTotalParamQuantity(recalculatedParameters);
    if (hasQuantityParam) {
        lineUpdate.quantity = MathUtils.plus(...recalculatedParameters.map(getTotalParamQuantity));
        lineUpdate.quantityExpression = null;
    }

    return updateLine(lines, lineUpdate);
}

function paramTreeContainsKey(parameters: BudgetLineParameterDto[] | undefined | null, key: string): boolean {
    if (!parameters) return false;
    for (const p of parameters) {
        if (p.key === key) return true;
        if (paramTreeContainsKey(p.children, key)) return true;
        if (p.elements) {
            for (const el of p.elements) {
                if (paramTreeContainsKey(el.parameters, key)) return true;
            }
        }
        if (p.minMax?.elements) {
            for (const el of p.minMax.elements) {
                if (paramTreeContainsKey(el.parameters, key)) return true;
            }
        }
    }
    return false;
}

function updateLineParameters(parameters: BudgetLineParameterDto[], updatedParameter: AtLeast<BudgetLineParameterDto, 'key'>): BudgetLineParameterDto[] {
    const targetKey = updatedParameter.key;
    return parameters.map(parameter => {
        if (parameter.key === targetKey) {
            return {
                ...parameter,
                ...updatedParameter,
            };
        }

        // Short-circuit: if the target key isn't anywhere in this subtree,
        // keep the original reference so memoization downstream stays intact.
        const inChildren = paramTreeContainsKey(parameter.children, targetKey as string);
        const inElements = !inChildren && !!parameter.elements?.some(el => paramTreeContainsKey(el.parameters, targetKey as string));
        const inMinMax = !inChildren && !inElements && !!parameter.minMax?.elements?.some(el => paramTreeContainsKey(el.parameters, targetKey as string));

        if (!inChildren && !inElements && !inMinMax) {
            return parameter;
        }

        return {
            ...parameter,
            children: inChildren ? updateLineParameters(parameter.children, updatedParameter) : parameter.children,
            elements: inElements
                ? (parameter.elements?.map(product =>
                    paramTreeContainsKey(product.parameters, targetKey as string)
                        ? { ...product, parameters: updateLineParameters(product.parameters ?? [], updatedParameter) }
                        : product
                ) ?? [])
                : (parameter.elements ?? []),
            minMax: parameter.minMax
                ? (inMinMax
                    ? {
                        ...parameter.minMax,
                        elements: parameter.minMax.elements?.map(product =>
                            paramTreeContainsKey(product.parameters, targetKey as string)
                                ? { ...product, parameters: updateLineParameters(product.parameters ?? [], updatedParameter) }
                                : product
                        ) ?? [],
                    }
                    : parameter.minMax)
                : parameter.minMax,
        };
    });
}

function moveLine(lines: BudgetLineDto[], lineKey: string, direction: 'up' | 'down') {
    const lineIndex = lines.findIndex(x => x.key === lineKey);
    if (lineIndex === -1) {
        return lines;
    }

    let newIndex: number | null = lineIndex;

    if (direction === 'up') {
        if (lineIndex === 0) {
            return lines; // Already at the top
        }
        newIndex = getPreviousIndexWithSameParentKey(lines, lineIndex);
    } else if (direction === 'down') {
        if (lineIndex === lines.length - 1) {
            return lines; // Already at the bottom
        }
        newIndex = getNextIndexWithSameParentKey(lines, lineIndex);
    } else {
        return lines; // Invalid direction
    }

    if (newIndex === null) {
        return lines;
    }

    // const movedLines = arrayMove(lines, lineIndex, newIndex);
    // return movedLines;

    // Get the subtree that needs to be moved
    const subtree = getSubtreeToMove(lines, lineIndex);
    const newLines = lines.filter(line => !subtree.includes(line));

    // Calculate the new position for the subtree
    let targetIndex = newIndex;
    if (direction === 'down') {
        targetIndex += subtree.length;
    }

    // Insert the subtree at the new position
    newLines.splice(targetIndex, 0, ...subtree);

    return newLines;
}

function getSubtreeToMove(lines: BudgetLineDto[], startIndex: number): BudgetLineDto[] {
    const startKey = lines[startIndex].key;
    const subtree = [lines[startIndex]];

    for (let i = startIndex + 1; i < lines.length; i++) {
        if (lines[i].parentKey === startKey) {
            subtree.push(...getSubtreeToMove(lines, i));
        }
    }

    return subtree;
}

export function getNextIndexWithSameParentKey(lines: BudgetLineDto[], currentIndex: number): number | null {
    if (currentIndex < 0 || currentIndex >= lines.length) {
        return null;
    }

    const currentParentKey = lines[currentIndex].parentKey;

    for (let i = currentIndex + 1; i < lines.length; i++) {
        if (lines[i].parentKey === currentParentKey) {
            return i;
        }
    }

    return null;
}

export function getPreviousIndexWithSameParentKey(array: BudgetLineDto[], currentIndex: number): number | null {
    if (currentIndex < 0 || currentIndex >= array.length) {
        return null;
    }

    const currentParentKey = array[currentIndex].parentKey;

    for (let i = currentIndex - 1; i >= 0; i--) {
        if (array[i].parentKey === currentParentKey) {
            return i;
        }
    }

    return null;
}

export function canMoveUp(lines: BudgetLineDto[], lineKey: string | null): boolean {
    if (lineKey === null) {
        return false;
    }

    const lineIndex = lines.findIndex(x => x.key === lineKey);
    if (lineIndex <= -1) {
        return false;
    }

    const moveIndex = getPreviousIndexWithSameParentKey(lines, lineIndex);

    return moveIndex != null && moveIndex !== lineIndex;
}

export function canMoveDown(lines: BudgetLineDto[], lineKey: string | null): boolean {
    if (lineKey === null) {
        return false;
    }

    const lineIndex = lines.findIndex(x => x.key === lineKey);
    if (lineIndex <= -1) {
        return false;
    }

    const moveIndex = getNextIndexWithSameParentKey(lines, lineIndex);

    return moveIndex != null && moveIndex !== lineIndex;
}

/**
 * Returns the subset of selectedKeys whose ancestors are not also selected.
 * Used so that "selected parent + selected child" multi-selections behave as if
 * only the parent was selected for move/copy/paste operations — the child is
 * implicitly carried by the parent's subtree.
 */
export function getTopLevelSelectedKeys(lines: BudgetLineDto[], selectedKeys: string[]): string[] {
    if (selectedKeys.length <= 1) return [...selectedKeys];
    const selectedSet = new Set(selectedKeys);
    const lineByKey = new Map<string, BudgetLineDto>();
    for (const l of lines) lineByKey.set(l.key, l);
    return selectedKeys.filter(key => {
        let parentKey = lineByKey.get(key)?.parentKey;
        while (parentKey) {
            if (selectedSet.has(parentKey)) return false;
            parentKey = lineByKey.get(parentKey)?.parentKey;
        }
        return true;
    });
}

export function allSelectedShareParent(lines: BudgetLineDto[], selectedKeys: string[]): boolean {
    const topLevel = getTopLevelSelectedKeys(lines, selectedKeys);
    if (topLevel.length <= 1) {
        return true;
    }
    const lineByKey = new Map<string, BudgetLineDto>();
    for (const l of lines) lineByKey.set(l.key, l);
    const first = lineByKey.get(topLevel[0]);
    if (!first) return false;
    const parent = first.parentKey ?? null;
    for (let i = 1; i < topLevel.length; i++) {
        const line = lineByKey.get(topLevel[i]);
        if (!line || (line.parentKey ?? null) !== parent) return false;
    }
    return true;
}

/**
 * True if every top-level selected line is at the same tree depth, regardless of
 * sharing the same parent. Used to gate cross-section drag-and-drop — lines at
 * the same level can be relocated together under a new parent.
 */
export function allSelectedAtSameDepth(lines: BudgetLineDto[], selectedKeys: string[]): boolean {
    const topLevel = getTopLevelSelectedKeys(lines, selectedKeys);
    if (topLevel.length <= 1) {
        return true;
    }
    const lineByKey = new Map<string, BudgetLineDto>();
    for (const l of lines) lineByKey.set(l.key, l);
    const depthOf = (key: string): number => {
        let d = 0;
        let parentKey = lineByKey.get(key)?.parentKey;
        while (parentKey) {
            d++;
            parentKey = lineByKey.get(parentKey)?.parentKey;
        }
        return d;
    };
    const firstDepth = depthOf(topLevel[0]);
    return topLevel.every(k => depthOf(k) === firstDepth);
}

export function canMoveLinesUp(lines: BudgetLineDto[], selectedKeys: string[]): boolean {
    const topLevel = getTopLevelSelectedKeys(lines, selectedKeys);
    if (!topLevel.length || !allSelectedShareParent(lines, selectedKeys)) {
        return false;
    }
    return topLevel.every(k => canMoveUp(lines, k));
}

export function canMoveLinesDown(lines: BudgetLineDto[], selectedKeys: string[]): boolean {
    const topLevel = getTopLevelSelectedKeys(lines, selectedKeys);
    if (!topLevel.length || !allSelectedShareParent(lines, selectedKeys)) {
        return false;
    }
    return topLevel.every(k => canMoveDown(lines, k));
}

/**
 * Move a contiguous group of sibling lines up or down by one position. Only valid
 * when all top-level selected keys share the same parentKey. Processes top-down
 * for 'up' and bottom-up for 'down' so the relative order is preserved.
 */
function moveLinesGroup(lines: BudgetLineDto[], selectedKeys: string[], direction: 'up' | 'down'): BudgetLineDto[] {
    if (!selectedKeys.length) return lines;
    if (!allSelectedShareParent(lines, selectedKeys)) return lines;

    const topLevel = getTopLevelSelectedKeys(lines, selectedKeys);
    if (!topLevel.length) return lines;

    const parentKey = lines.find(l => l.key === topLevel[0])?.parentKey ?? null;
    const siblings = lines.filter(l => (l.parentKey ?? null) === parentKey);
    const topLevelSet = new Set(topLevel);
    const orderedSelected = siblings.filter(s => topLevelSet.has(s.key));
    if (!orderedSelected.length) return lines;

    if (direction === 'up') {
        if (siblings.findIndex(s => s.key === orderedSelected[0].key) === 0) return lines;
        let next = lines;
        for (const sibling of orderedSelected) {
            next = moveLine(next, sibling.key, 'up');
        }
        return next;
    } else {
        const lastSelected = orderedSelected[orderedSelected.length - 1];
        if (siblings.findIndex(s => s.key === lastSelected.key) === siblings.length - 1) return lines;
        let next = lines;
        for (let i = orderedSelected.length - 1; i >= 0; i--) {
            next = moveLine(next, orderedSelected[i].key, 'down');
        }
        return next;
    }
}

/**
 * After a single-line drag has produced `newLines`, move the remaining selected lines
 * (`otherKeys`) so they sit contiguously immediately after the dragged anchor's subtree
 * under the same new parent, preserving their relative order from `newLines`.
 */
export function relocateLinesAfter(
    newLines: BudgetLineDto[],
    anchorKey: string,
    otherKeys: string[],
    newParentKey: string | null,
): BudgetLineDto[] {
    if (!otherKeys.length) return newLines;
    const otherSet = new Set(otherKeys);

    // Capture the requested move order in the visual order of `newLines`.
    const orderedOthers = newLines.filter(l => otherSet.has(l.key)).map(l => l.key);
    if (!orderedOthers.length) return newLines;

    // Step 1: extract each "other" subtree from newLines (key + descendants).
    const subtrees: BudgetLineDto[][] = [];
    let workingLines = [...newLines];
    for (const key of orderedOthers) {
        const idx = workingLines.findIndex(l => l.key === key);
        if (idx < 0) continue;
        const subtree = getSubtreeToMove(workingLines, idx);
        // Re-parent the root of this subtree to the dragged anchor's new parent.
        const rePar = subtree.map((l, i) => (
            i === 0 ? { ...l, parentKey: newParentKey } : l
        ));
        subtrees.push(rePar);
        workingLines = workingLines.filter(l => !subtree.includes(l));
    }
    if (!subtrees.length) return newLines;

    // Step 2: insert all extracted subtrees right after the anchor's subtree.
    const anchorIndex = workingLines.findIndex(l => l.key === anchorKey);
    if (anchorIndex < 0) return newLines;
    const anchorSubtree = getSubtreeToMove(workingLines, anchorIndex);
    const insertAt = anchorIndex + anchorSubtree.length;

    const flat = subtrees.reduce<BudgetLineDto[]>((acc, s) => acc.concat(s), []);
    return [
        ...workingLines.slice(0, insertAt),
        ...flat,
        ...workingLines.slice(insertAt),
    ];
}

export default rootReducer;
